import { useRef, useEffect, useCallback, useState } from 'react';
import * as fabric from 'fabric';
import type {
  EditorTool,
  BrushSettings,
  ShapeSettings,
  TextSettings,
  FilterSettings,
  ShapeType,
  MarqueeShape,
} from '../types';
import { computeMarqueeBounds } from '../lib/marquee-bounds';

const MARQUEE_STROKE_SCREEN_PX = 1;
const MARQUEE_DASH_SCREEN_PX = [5, 3];
const MARQUEE_FILL_ALPHA = 0.1;
const MARQUEE_DEFAULT_COLOR = '#0078d7';

// Paint-bucket SVG (Lucide-style) used as the fill tool's cursor. Encoded
// inline so it ships with the bundle without an extra asset round-trip.
// White fill + black stroke keep it visible on any canvas color. Hotspot is
// at the bucket's lower-left tip where paint conceptually drips.
const FILL_CURSOR_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">` +
  `<path fill="white" d="m19 11-8-8-8.6 8.6a2 2 0 0 0 0 2.8l5.2 5.2c.8.8 2 .8 2.8 0L19 11Z"/>` +
  `<path d="m5 2 5 5"/>` +
  `<path d="M2 13h15"/>` +
  `<path fill="white" d="M22 20a2 2 0 1 1-4 0c0-1.6 1.7-2.4 2-4 .3 1.6 2 2.4 2 4Z"/>` +
  `</svg>`;
const FILL_CURSOR = `url("data:image/svg+xml;utf8,${encodeURIComponent(FILL_CURSOR_SVG)}") 4 20, crosshair`;

const MARQUEE_STYLE: Partial<fabric.FabricObjectProps> = {
  strokeWidth: MARQUEE_STROKE_SCREEN_PX,
  strokeDashArray: [...MARQUEE_DASH_SCREEN_PX],
  strokeUniform: true,
  selectable: false,
  evented: false,
  excludeFromExport: true,
};

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return hex;
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function applyMarqueeColor(obj: fabric.Object, color: string): void {
  obj.set({
    stroke: color,
    fill: hexToRgba(color, MARQUEE_FILL_ALPHA),
  });
}

const CROP_RECT_STYLE: Partial<fabric.FabricObjectProps> = {
  fill: 'rgba(0, 120, 215, 0.08)',
  stroke: '#0078d7',
  strokeUniform: true,
  hasControls: true,
  hasBorders: true,
  borderColor: '#0078d7',
  borderDashArray: [5, 3],
  lockRotation: true,
  lockScalingFlip: true,
  selectable: true,
  evented: true,
  excludeFromExport: true,
  objectCaching: false,
  cornerColor: '#ffffff',
  cornerStrokeColor: '#0078d7',
  cornerSize: 12,
  transparentCorners: false,
  cornerStyle: 'rect',
};

/**
 * Build a binary selection mask using either flood-fill (contiguous) or a global
 * threshold scan (non-contiguous). The tolerance is the maximum allowed
 * per-channel difference (0–255, Photoshop-style).
 */
function computeWandMask(
  imgData: ImageData,
  startX: number,
  startY: number,
  tolerance: number,
  contiguous: boolean,
): Uint8Array {
  const { data, width, height } = imgData;
  const mask = new Uint8Array(width * height);

  if (startX < 0 || startX >= width || startY < 0 || startY >= height) return mask;

  const startIdx = (startY * width + startX) * 4;
  const sr = data[startIdx];
  const sg = data[startIdx + 1];
  const sb = data[startIdx + 2];

  const matches = (i: number): boolean => {
    const di = i * 4;
    return (
      Math.abs(data[di] - sr) <= tolerance &&
      Math.abs(data[di + 1] - sg) <= tolerance &&
      Math.abs(data[di + 2] - sb) <= tolerance
    );
  };

  if (contiguous) {
    const queue: number[] = [startY * width + startX];
    mask[startY * width + startX] = 1;
    let head = 0;
    while (head < queue.length) {
      const idx = queue[head++];
      const x = idx % width;
      const y = (idx - x) / width;
      const neighbors: [number, number][] = [
        [x + 1, y],
        [x - 1, y],
        [x, y + 1],
        [x, y - 1],
      ];
      for (const [nx, ny] of neighbors) {
        if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
        const nidx = ny * width + nx;
        if (mask[nidx]) continue;
        if (matches(nidx)) {
          mask[nidx] = 1;
          queue.push(nidx);
        }
      }
    }
  } else {
    for (let i = 0; i < width * height; i++) {
      if (matches(i)) mask[i] = 1;
    }
  }

  return mask;
}

/**
 * Trace the boundary contours of a binary mask. Walks pixel-corner edges
 * between selected and unselected cells, then stitches edges into closed
 * polygons. Returns one polygon per disconnected region (and one per hole).
 */
function traceContours(mask: Uint8Array, w: number, h: number): { x: number; y: number }[][] {
  const isSelected = (x: number, y: number): boolean =>
    x >= 0 && x < w && y >= 0 && y < h && mask[y * w + x] === 1;

  type Pt = { x: number; y: number };
  const ptKey = (p: Pt) => `${p.x},${p.y}`;
  const edgeKey = (a: Pt, b: Pt) =>
    a.x < b.x || (a.x === b.x && a.y < b.y)
      ? `${a.x},${a.y}-${b.x},${b.y}`
      : `${b.x},${b.y}-${a.x},${a.y}`;

  const edges: [Pt, Pt][] = [];
  const adj = new Map<string, [Pt, Pt][]>();

  const addEdge = (a: Pt, b: Pt) => {
    const e: [Pt, Pt] = [a, b];
    edges.push(e);
    const ka = ptKey(a);
    const kb = ptKey(b);
    if (!adj.has(ka)) adj.set(ka, []);
    if (!adj.has(kb)) adj.set(kb, []);
    adj.get(ka)!.push(e);
    adj.get(kb)!.push(e);
  };

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!isSelected(x, y)) continue;
      if (!isSelected(x, y - 1)) addEdge({ x, y }, { x: x + 1, y });
      if (!isSelected(x + 1, y)) addEdge({ x: x + 1, y }, { x: x + 1, y: y + 1 });
      if (!isSelected(x, y + 1)) addEdge({ x, y: y + 1 }, { x: x + 1, y: y + 1 });
      if (!isSelected(x - 1, y)) addEdge({ x, y }, { x, y: y + 1 });
    }
  }

  const visited = new Set<string>();
  const contours: Pt[][] = [];

  for (const startEdge of edges) {
    if (visited.has(edgeKey(startEdge[0], startEdge[1]))) continue;
    const polygon: Pt[] = [startEdge[0]];
    let currentEdge = startEdge;
    let currentPoint = startEdge[0];
    while (true) {
      visited.add(edgeKey(currentEdge[0], currentEdge[1]));
      const nextPoint =
        currentEdge[0].x === currentPoint.x && currentEdge[0].y === currentPoint.y
          ? currentEdge[1]
          : currentEdge[0];
      polygon.push(nextPoint);
      const candidates = adj.get(ptKey(nextPoint)) || [];
      const nextEdge = candidates.find((e) => !visited.has(edgeKey(e[0], e[1])));
      if (!nextEdge) break;
      currentEdge = nextEdge;
      currentPoint = nextPoint;
    }
    if (polygon.length >= 3) contours.push(polygon);
  }

  return contours;
}

function buildCropRect(canvas: fabric.Canvas, width: number, height: number): fabric.Rect {
  const zoom = canvas.getZoom() || 1;
  const rect = new fabric.Rect({
    ...CROP_RECT_STYLE,
    left: 0,
    top: 0,
    width,
    height,
    strokeWidth: 1 / zoom,
    strokeDashArray: [5 / zoom, 3 / zoom],
  });
  (rect as unknown as { _isCropRect: boolean })._isCropRect = true;
  return rect;
}

function tagAsMarquee(obj: fabric.Object): void {
  (obj as unknown as { _isMarquee: boolean })._isMarquee = true;
}

function isMarqueeObject(obj: fabric.Object): boolean {
  return (obj as unknown as { _isMarquee?: boolean })._isMarquee === true;
}

function setObjectLayerId(obj: fabric.Object, layerId: string): void {
  (obj as unknown as { layerId: string }).layerId = layerId;
}

function getObjectLayerId(obj: fabric.Object): string | undefined {
  return (obj as unknown as { layerId?: string }).layerId;
}

/**
 * Build a curved Fabric Path that text can follow. The slider value (-100..100)
 * scales an apex offset proportional to the text width; positive arches up
 * (smile), negative arches down (frown). Returns null for a flat (0) value or
 * non-positive width.
 */
function buildTextArchPath(width: number, arch: number): fabric.Path | null {
  if (arch === 0 || width <= 0) return null;
  // Apex height ≈ 25% of text width at full slider; control point is twice
  // the apex offset from the chord (quadratic-bezier midpoint property).
  const apex = (arch / 100) * (width * 0.25);
  const controlY = -apex * 2;
  const pathData = `M 0 0 Q ${width / 2} ${controlY} ${width} 0`;
  const path = new fabric.Path(pathData, {
    fill: '',
    stroke: '',
    objectCaching: false,
    visible: false,
  });
  return path;
}

function tagAsBackground(obj: fabric.Object): void {
  (obj as unknown as { _isBackground: boolean })._isBackground = true;
}

function isBackgroundImage(obj: fabric.Object): boolean {
  return (obj as unknown as { _isBackground?: boolean })._isBackground === true;
}

/**
 * Convert a Fabric image-like element (`HTMLImageElement` or
 * `HTMLCanvasElement`) into a canvas of the same size with the image content
 * drawn onto it. Returns the existing element if it's already a canvas.
 *
 * Used to make a FabricImage's underlying source paintable: callers can write
 * to the returned canvas's 2D context, mark the FabricImage dirty, and the
 * change shows up on the next render.
 */
function ensureCanvasElement(
  el: HTMLImageElement | HTMLCanvasElement | HTMLVideoElement,
  fallbackWidth: number,
  fallbackHeight: number,
): HTMLCanvasElement {
  if (el instanceof HTMLCanvasElement) return el;
  const canvas = document.createElement('canvas');
  canvas.width = (el as HTMLImageElement).naturalWidth || fallbackWidth || 1;
  canvas.height = (el as HTMLImageElement).naturalHeight || fallbackHeight || 1;
  const ctx = canvas.getContext('2d');
  if (ctx) ctx.drawImage(el, 0, 0);
  return canvas;
}

/**
 * Convert a stroke render (where any non-transparent pixel is part of the
 * stroke shape) into an alpha mask whose every stroke pixel is opaque at
 * `opacity` (0..1). Used by the eraser bake to decouple the *preview*
 * color's alpha (a fixed semi-transparent gray, picked for visibility on
 * any background) from the bake's actual erase strength.
 *
 * `opacity = 1` produces a fully-opaque mask → destination-out fully
 * erases. `opacity = 0.5` halves the alpha removed at each pixel. The
 * input stroke shape is preserved; only the alpha values are normalized.
 */
function buildEraserMask(strokeEl: HTMLCanvasElement, opacity: number): HTMLCanvasElement {
  const w = strokeEl.width;
  const h = strokeEl.height;
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');
  if (!ctx) return out;
  ctx.drawImage(strokeEl, 0, 0);
  const data = ctx.getImageData(0, 0, w, h);
  const targetAlpha = Math.round(Math.max(0, Math.min(1, opacity)) * 255);
  const px = data.data;
  for (let i = 3; i < px.length; i += 4) {
    if (px[i] > 0) px[i] = targetAlpha;
  }
  ctx.putImageData(data, 0, 0);
  return out;
}

/**
 * Apply the active marquee's geometry as a clip on the given 2D context.
 * Wand selections use their cached binary mask via destination-in compositing
 * (caller must do that on a temp canvas — see `bakeStrokeOntoSurface`).
 *
 * Returns true if a clip was applied; false if no marquee is active or its
 * geometry isn't usable.
 */
function applyMarqueeClipToContext(
  ctx: CanvasRenderingContext2D,
  m: {
    shape: MarqueeShape;
    obj: fabric.Object | null;
    points: { x: number; y: number }[];
    isDrawing: boolean;
  },
): boolean {
  if (!m.obj || m.isDrawing) return false;
  ctx.beginPath();
  if (m.shape === 'rect') {
    const r = m.obj as fabric.Rect;
    ctx.rect(r.left ?? 0, r.top ?? 0, r.width ?? 0, r.height ?? 0);
  } else if (m.shape === 'ellipse') {
    const e = m.obj as fabric.Ellipse;
    const rx = e.rx ?? 0;
    const ry = e.ry ?? 0;
    ctx.ellipse((e.left ?? 0) + rx, (e.top ?? 0) + ry, rx, ry, 0, 0, Math.PI * 2);
  } else if ((m.shape === 'lasso' || m.shape === 'polygon') && m.points.length >= 3) {
    ctx.moveTo(m.points[0].x, m.points[0].y);
    for (let i = 1; i < m.points.length; i++) ctx.lineTo(m.points[i].x, m.points[i].y);
    ctx.closePath();
  } else {
    // wand handled via caller's destination-in path; fallthrough returns false
    return false;
  }
  ctx.clip();
  return true;
}

/**
 * Rasterize a finalized marquee selection to a binary mask of size
 * `width × height`. `mask[y*width+x] === 1` iff scene pixel (x,y) is inside
 * the marquee. Returns null if the marquee isn't finalized or its geometry
 * isn't usable. Used by the fill tool to constrain flood-fill results to
 * the marquee region.
 */
function buildMarqueeMask(
  m: {
    shape: MarqueeShape;
    obj: fabric.Object | null;
    extraObjs: fabric.Object[];
    points: { x: number; y: number }[];
    isDrawing: boolean;
    wandMaskCanvas: HTMLCanvasElement | null;
  },
  width: number,
  height: number,
): Uint8Array | null {
  if (!m.obj || m.isDrawing) return null;

  // Wand: alpha channel of the cached mask canvas IS the binary mask.
  if (m.shape === 'wand' && m.wandMaskCanvas) {
    const ctx = m.wandMaskCanvas.getContext('2d');
    if (!ctx) return null;
    const data = ctx.getImageData(0, 0, width, height).data;
    const mask = new Uint8Array(width * height);
    for (let i = 0; i < mask.length; i++) {
      mask[i] = data[i * 4 + 3] > 0 ? 1 : 0;
    }
    return mask;
  }

  // Other shapes (rect / ellipse / lasso / polygon): rasterize by filling
  // the clipped region on a fresh canvas, then read its alpha channel.
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  const clipped = applyMarqueeClipToContext(ctx, m);
  if (!clipped) return null;
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, width, height);
  const data = ctx.getImageData(0, 0, width, height).data;
  const mask = new Uint8Array(width * height);
  for (let i = 0; i < mask.length; i++) {
    mask[i] = data[i * 4 + 3] > 0 ? 1 : 0;
  }
  return mask;
}

/** Tools that should preserve the active marquee when switched to. */
const MARQUEE_PRESERVING_TOOLS: ReadonlySet<EditorTool> = new Set<EditorTool>([
  'marquee',
  'move',
  'draw',
  'eraser',
  'fill',
  'eyedropper',
]);

/**
 * Counter-scale stroke width and dash pattern by the current canvas zoom so the
 * marquee outline stays at a constant screen-pixel size regardless of zoom level.
 * Without this, dashed strokes shrink to fractions of a pixel when zoomed out.
 */
function applyMarqueeStroke(canvas: fabric.Canvas, obj: fabric.Object): void {
  const zoom = canvas.getZoom() || 1;
  obj.set({
    strokeWidth: MARQUEE_STROKE_SCREEN_PX / zoom,
    strokeDashArray: MARQUEE_DASH_SCREEN_PX.map((v) => v / zoom),
  });
}

const POLYGON_CLOSE_THRESHOLD_PX = 10;

interface UseFabricCanvasOptions {
  canvasElRef: React.RefObject<HTMLCanvasElement | null>;
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  onObjectModified?: () => void;
  onSelectionChanged?: (objectIds: string[]) => void;
  /** Fired when an object on the canvas becomes the active object — receives
   *  the layerId of that object (or null if it has none). Not fired on clears. */
  onLayerSelected?: (layerId: string | null) => void;
  /** Fired when the user clicks an empty area while the Shape tool is active.
   *  EditorPage handles by creating a layer and adding a new shape at the
   *  click position. */
  onShapeToolClick?: (point: { x: number; y: number }) => void;
  /** Same idea for the Text tool — fired on empty-area click. */
  onTextToolClick?: (point: { x: number; y: number }) => void;
  onCanvasDimensions?: (width: number, height: number) => void;
  /**
   * Fired on every mouse:move with cursor scene-coords. `color` is the live
   * pixel color under the cursor IFF the eyedropper tool is active; otherwise
   * `null` so callers can leave the displayed color pinned to its prior value.
   */
  onCursorMove?: (x: number, y: number, color: string | null) => void;
  /** Fired when the eyedropper tool clicks a pixel — used to set brush/fill color. */
  onColorPicked?: (color: string) => void;
  onPathCreated?: () => void;
  onZoomChanged?: (zoom: number) => void;
}

function closePolygonMarquee(
  canvas: fabric.Canvas,
  m: {
    obj: fabric.Object | null;
    points: { x: number; y: number }[];
    isDrawing: boolean;
  },
  color: string,
): void {
  if (m.obj) canvas.remove(m.obj);
  const closed = new fabric.Polygon(m.points, MARQUEE_STYLE);
  tagAsMarquee(closed);
  applyMarqueeColor(closed, color);
  applyMarqueeStroke(canvas, closed);
  canvas.add(closed);
  m.obj = closed;
  m.isDrawing = false;
}

/**
 * Recreate the live-drawing polyline preview with the latest points. Mutating
 * `points` on an existing Polyline doesn't recalculate its bounding box, so
 * new points outside the original bbox get clipped on render. Replacing the
 * object outright keeps the preview in sync with the captured path.
 */
function replaceMarqueePolyline(
  canvas: fabric.Canvas,
  m: {
    obj: fabric.Object | null;
    points: { x: number; y: number }[];
  },
  color: string,
): void {
  if (m.obj) canvas.remove(m.obj);
  const poly = new fabric.Polyline(m.points, MARQUEE_STYLE);
  tagAsMarquee(poly);
  applyMarqueeColor(poly, color);
  applyMarqueeStroke(canvas, poly);
  canvas.add(poly);
  m.obj = poly;
}

function clientPointFromEvent(e: Event): { x: number; y: number } {
  if ('touches' in e && (e as TouchEvent).touches.length > 0) {
    const t = (e as TouchEvent).touches[0];
    return { x: t.clientX, y: t.clientY };
  }
  if ('changedTouches' in e && (e as TouchEvent).changedTouches.length > 0) {
    const t = (e as TouchEvent).changedTouches[0];
    return { x: t.clientX, y: t.clientY };
  }
  const me = e as MouseEvent;
  return { x: me.clientX ?? 0, y: me.clientY ?? 0 };
}

/** Viewport info for scrollbar indicators */
export interface ViewportInfo {
  thumbWidthRatio: number;
  thumbHeightRatio: number;
  scrollXRatio: number;
  scrollYRatio: number;
  showH: boolean;
  showV: boolean;
}

export function useFabricCanvas({
  canvasElRef,
  wrapperRef,
  onObjectModified,
  onSelectionChanged,
  onLayerSelected,
  onShapeToolClick,
  onTextToolClick,
  onCanvasDimensions,
  onCursorMove,
  onColorPicked,
  onPathCreated,
  onZoomChanged,
}: UseFabricCanvasOptions) {
  const canvasRef = useRef<fabric.Canvas | null>(null);
  const backgroundImageRef = useRef<fabric.FabricImage | null>(null);
  const imageDimsRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });
  const activeToolRef = useRef<EditorTool>('move');
  const marqueeRef = useRef<{
    shape: MarqueeShape;
    obj: fabric.Object | null;
    /** Additional contour polygons for shapes (like wand) that produce multiple regions. */
    extraObjs: fabric.Object[];
    /** Cached binary mask canvas for wand selections — used by the eraser path. */
    wandMaskCanvas: HTMLCanvasElement | null;
    /** Wand-mode tolerance and contiguous flag, latched at click time. */
    wandTolerance: number;
    wandContiguous: boolean;
    points: { x: number; y: number }[];
    startX: number;
    startY: number;
    isDrawing: boolean;
  }>({
    shape: 'rect',
    obj: null,
    extraObjs: [],
    wandMaskCanvas: null,
    wandTolerance: 32,
    wandContiguous: true,
    points: [],
    startX: 0,
    startY: 0,
    isDrawing: false,
  });
  const panRef = useRef<{ isPanning: boolean; lastX: number; lastY: number }>({
    isPanning: false,
    lastX: 0,
    lastY: 0,
  });
  const pinchRef = useRef<{ isPinching: boolean; startDist: number; startZoom: number }>({
    isPinching: false,
    startDist: 0,
    startZoom: 1,
  });
  const forceSquareRef = useRef(false);
  const marqueeColorRef = useRef(MARQUEE_DEFAULT_COLOR);
  const wandToleranceRef = useRef(32);
  const wandContiguousRef = useRef(true);
  const cropRectRef = useRef<fabric.Rect | null>(null);
  const activeLayerIdRef = useRef<string | null>(null);
  const brushColorRef = useRef('#000000');
  // Eraser strength as a fraction in [0, 1]. Decoupled from the eraser
  // brush's *preview* color (which is a fixed semi-transparent gray for
  // visibility) — the bake uses this value to set the alpha of the
  // destination-out mask, so 1.0 fully erases and 0 leaves pixels alone.
  // Mirrors brushSettings.opacity so the user has one stroke-strength
  // slider for both Draw and Eraser tools.
  const eraserOpacityRef = useRef(1);
  // Paint surfaces — one HTMLCanvasElement per paint-capable layer, wrapped
  // in a Fabric Image. New strokes render onto the active layer's surface.
  const paintSurfacesRef = useRef(
    new Map<string, { canvas: HTMLCanvasElement; fabricImg: fabric.FabricImage }>(),
  );
  // Forward-reference holder for ensurePaintSurface so callbacks defined
  // earlier in the file (e.g. deleteSelected) can call it without triggering
  // a use-before-declaration error.
  const ensurePaintSurfaceRef = useRef<
    | ((layerId: string) => { canvas: HTMLCanvasElement; fabricImg: fabric.FabricImage } | null)
    | null
  >(null);
  // Mirror every parent-supplied callback into a ref so the init-only
  // useEffect (which sets up Fabric event handlers exactly once) always
  // dispatches to the latest closure. Without this, callers would have to
  // wrap each callback in a stable useCallback or risk stale state inside
  // the handler.
  const onZoomChangedRef = useRef(onZoomChanged);
  onZoomChangedRef.current = onZoomChanged;
  const onLayerSelectedRef = useRef(onLayerSelected);
  onLayerSelectedRef.current = onLayerSelected;
  const onColorPickedRef = useRef(onColorPicked);
  onColorPickedRef.current = onColorPicked;
  const onShapeToolClickRef = useRef(onShapeToolClick);
  onShapeToolClickRef.current = onShapeToolClick;
  const onTextToolClickRef = useRef(onTextToolClick);
  onTextToolClickRef.current = onTextToolClick;
  const onObjectModifiedRef = useRef(onObjectModified);
  onObjectModifiedRef.current = onObjectModified;
  const onSelectionChangedRef = useRef(onSelectionChanged);
  onSelectionChangedRef.current = onSelectionChanged;
  const onCursorMoveRef = useRef(onCursorMove);
  onCursorMoveRef.current = onCursorMove;
  const onPathCreatedRef = useRef(onPathCreated);
  onPathCreatedRef.current = onPathCreated;
  const onCanvasDimensionsRef = useRef(onCanvasDimensions);
  onCanvasDimensionsRef.current = onCanvasDimensions;

  /**
   * Resolve the background image. Returns the cached ref if it's still on the
   * canvas; otherwise scans the canvas for a non-selectable FabricImage,
   * rebinds the ref + dims, and returns it. Returns null if the canvas has no
   * image at all.
   *
   * Use this rather than reading `backgroundImageRef.current` directly — it
   * tolerates strict-mode double-mounts and any other path that drops the ref
   * out of sync with the actual canvas state.
   */
  const getBackgroundImage = useCallback((): fabric.FabricImage | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const current = backgroundImageRef.current;
    if (current && canvas.contains(current)) return current;

    // Prefer the explicit `_isBackground` tag; fall back to the legacy
    // "non-selectable FabricImage" check for sessions saved before tagging.
    const found = canvas
      .getObjects()
      .find(
        (o) =>
          o instanceof fabric.FabricImage &&
          (isBackgroundImage(o) || !(o as fabric.FabricImage & { selectable: boolean }).selectable),
      ) as fabric.FabricImage | undefined;

    if (found) {
      tagAsBackground(found);
      backgroundImageRef.current = found;
      imageDimsRef.current = {
        width: found.width || 1,
        height: found.height || 1,
      };
      return found;
    }

    backgroundImageRef.current = null;
    return null;
  }, []);
  const updateViewportInfoRef = useRef<() => void>(() => {});

  const [viewport, setViewport] = useState<ViewportInfo>({
    thumbWidthRatio: 1,
    thumbHeightRatio: 1,
    scrollXRatio: 0,
    scrollYRatio: 0,
    showH: false,
    showV: false,
  });

  // Reactive flags that drive Cut / Copy / Paste toolbar button enable
  // states. `hasMarqueeSelection` flips on every marquee finalize / clear;
  // `hasClipboard` flips when the in-memory clipboard ref is populated by
  // copy or cut. The clipboard ref itself is non-reactive so the heavy
  // bitmap doesn't trigger re-renders.
  const [hasMarqueeSelection, setHasMarqueeSelection] = useState(false);
  const [hasClipboard, setHasClipboard] = useState(false);
  const clipboardRef = useRef<{
    canvas: HTMLCanvasElement;
    left: number;
    top: number;
  } | null>(null);
  // Helper: re-evaluate `hasMarqueeSelection` from the current marquee
  // state. Called at every finalize and clear point so React state stays
  // in sync with the marqueeRef object identity.
  const refreshMarqueeSelectionState = useCallback(() => {
    const m = marqueeRef.current;
    setHasMarqueeSelection(!!m.obj && !m.isDrawing);
  }, []);

  /** Compute scrollbar indicator state from current viewport transform */
  const updateViewportInfo = useCallback(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;

    const vt = canvas.viewportTransform;
    if (!vt) return;

    const zoom = vt[0];
    const panX = vt[4];
    const panY = vt[5];
    const vpW = wrapper.clientWidth;
    const vpH = wrapper.clientHeight;
    const imgW = imageDimsRef.current.width;
    const imgH = imageDimsRef.current.height;

    if (!imgW || !imgH) {
      setViewport({
        thumbWidthRatio: 1,
        thumbHeightRatio: 1,
        scrollXRatio: 0,
        scrollYRatio: 0,
        showH: false,
        showV: false,
      });
      return;
    }

    const contentW = imgW * zoom;
    const contentH = imgH * zoom;

    const thumbWR = Math.min(1, vpW / contentW);
    const thumbHR = Math.min(1, vpH / contentH);

    const maxPanX = 0;
    const minPanX = vpW - contentW;
    const scrollXR = minPanX < maxPanX ? (maxPanX - panX) / (maxPanX - minPanX) : 0;

    const maxPanY = 0;
    const minPanY = vpH - contentH;
    const scrollYR = minPanY < maxPanY ? (maxPanY - panY) / (maxPanY - minPanY) : 0;

    setViewport({
      thumbWidthRatio: thumbWR,
      thumbHeightRatio: thumbHR,
      scrollXRatio: Math.max(0, Math.min(1, scrollXR)),
      scrollYRatio: Math.max(0, Math.min(1, scrollYR)),
      showH: thumbWR < 0.99,
      showV: thumbHR < 0.99,
    });
  }, [wrapperRef]);

  // Keep ref in sync so event handlers in useEffect can call the latest version
  updateViewportInfoRef.current = updateViewportInfo;

  // Initialize canvas
  useEffect(() => {
    const canvasEl = canvasElRef.current;
    const wrapper = wrapperRef.current;
    if (!canvasEl || !wrapper) return;

    const canvas = new fabric.Canvas(canvasEl, {
      backgroundColor: 'transparent',
      selection: true,
      preserveObjectStacking: true,
    });

    canvasRef.current = canvas;
    // Expose to window for ad-hoc DevTools debugging.
    if (typeof window !== 'undefined') {
      (window as unknown as { __fiasCanvas?: fabric.Canvas }).__fiasCanvas = canvas;
    }

    // Force Fabric's auto-created wrapper to fill our container
    if (canvas.wrapperEl) {
      canvas.wrapperEl.style.position = 'absolute';
      canvas.wrapperEl.style.inset = '0';
      canvas.wrapperEl.style.width = '100%';
      canvas.wrapperEl.style.height = '100%';
    }

    // Canvas element always matches wrapper size
    const resize = () => {
      const w = wrapper.clientWidth;
      const h = wrapper.clientHeight;
      if (w > 0 && h > 0) {
        canvas.setDimensions({ width: w, height: h });
        canvas.renderAll();
      }
    };
    resize();

    const observer = new ResizeObserver(resize);
    observer.observe(wrapper);

    // Event listeners
    canvas.on('object:modified', () => onObjectModifiedRef.current?.());
    canvas.on('path:created', (e) => {
      const path = (e as { path?: fabric.Object }).path;
      if (!path) {
        onPathCreatedRef.current?.();
        return;
      }
      // Always remove the auto-added Fabric Path from the main canvas first;
      // bake takes the unbound path and renders it onto the active layer's
      // surface via a temporary StaticCanvas.
      canvas.remove(path);
      const layerId = activeLayerIdRef.current;
      if (layerId) {
        const isEraser = activeToolRef.current === 'eraser';
        bakeStrokeOntoSurface(path, layerId, isEraser);
      }
      // Synchronous render so the user sees the baked stroke in the same
      // frame the live preview was cleared.
      canvas.renderAll();
      onPathCreatedRef.current?.();
    });

    const fireSelection = (selected: fabric.FabricObject[] | undefined) => {
      const objs = selected ?? [];
      const ids = objs.map((o) => (o as fabric.FabricObject & { id?: string }).id || '');
      onSelectionChangedRef.current?.(ids);
      // Pull the layerId from the first selected object — sufficient for the
      // common single-object case; for multi-select, fall back to that object's
      // layer to drive the panel highlight.
      const layerId = objs.length > 0 ? (getObjectLayerId(objs[0]) ?? null) : null;
      onLayerSelectedRef.current?.(layerId);
    };

    // When a single non-bg object is clicked with the move tool, expand the
    // selection to include every other object on the same layer (e.g. the
    // layer's paint surface) so they translate together. Without this, drawing
    // baked into a shape layer's paint surface would stay behind when the
    // shape is moved.
    let isExpanding = false;
    const expandSelectionToLayer = (selected: fabric.FabricObject[] | undefined) => {
      if (isExpanding) return;
      if (activeToolRef.current !== 'move') return;
      if (!selected || selected.length !== 1) return;
      const target = selected[0];
      if (isBackgroundImage(target) || isMarqueeObject(target)) return;
      const layerId = getObjectLayerId(target);
      if (!layerId) return;
      const peers = canvas
        .getObjects()
        .filter(
          (o) =>
            o !== target &&
            getObjectLayerId(o) === layerId &&
            !isMarqueeObject(o) &&
            !isBackgroundImage(o),
        );
      if (peers.length === 0) return;
      const all = [target, ...peers];
      isExpanding = true;
      try {
        canvas.discardActiveObject();
        const sel = new fabric.ActiveSelection(all, { canvas });
        canvas.setActiveObject(sel);
        canvas.requestRenderAll();
      } finally {
        isExpanding = false;
      }
    };

    const handleSelection = (selected: fabric.FabricObject[] | undefined) => {
      fireSelection(selected);
      expandSelectionToLayer(selected);
    };
    canvas.on('selection:created', (e) => handleSelection(e.selected));
    canvas.on('selection:updated', (e) => handleSelection(e.selected));
    canvas.on('selection:cleared', () => onSelectionChangedRef.current?.([]));

    // Skip the GPU readback when the cursor stays on the same display pixel
    // (the displayed color/XY won't change anyway). Cuts redundant
    // `getImageData` calls during fast diagonal drags on hi-DPI canvases.
    let lastSampledPx = -1;
    let lastSampledPy = -1;
    canvas.on('mouse:move', (opt) => {
      if (!opt.e) return;
      // Scene point — image-space coords, used for the on-screen XY readout.
      const scenePoint = canvas.getScenePoint(opt.e);
      // Viewport point — display-canvas coords (post viewport transform).
      // ctx.getImageData reads from the underlying lower canvas in display
      // pixels, so we need viewport coords (not scene coords) here. Multiply
      // by the retina scaling factor to hit the right pixel on hi-DPI screens.
      const viewportPoint = canvas.getViewportPoint(opt.e);
      const dpr = canvas.getRetinaScaling?.() || 1;
      const px = Math.round(viewportPoint.x * dpr);
      const py = Math.round(viewportPoint.y * dpr);
      const lower = canvas.getContext();
      const w = lower.canvas.width;
      const h = lower.canvas.height;
      // Only sample the pixel when the eyedropper tool is active; for other
      // tools the displayed color stays pinned to whatever was last selected.
      let hex: string | null = null;
      if (
        activeToolRef.current === 'eyedropper' &&
        px >= 0 &&
        py >= 0 &&
        px < w &&
        py < h &&
        (px !== lastSampledPx || py !== lastSampledPy)
      ) {
        lastSampledPx = px;
        lastSampledPy = py;
        const pixel = lower.getImageData(px, py, 1, 1).data;
        hex = `#${pixel[0].toString(16).padStart(2, '0')}${pixel[1].toString(16).padStart(2, '0')}${pixel[2].toString(16).padStart(2, '0')}`;
      }
      onCursorMoveRef.current?.(Math.round(scenePoint.x), Math.round(scenePoint.y), hex);
    });

    // Mouse wheel zoom — zoom toward the cursor position
    canvas.on('mouse:wheel', (opt) => {
      const e = opt.e as WheelEvent;
      e.preventDefault();
      e.stopPropagation();

      const delta = e.deltaY;
      const vt = canvas.viewportTransform;
      if (!vt) return;

      const currentZoom = vt[0];
      const factor = delta > 0 ? 0.9 : 1.1;
      const newZoom = Math.max(0.1, Math.min(10, currentZoom * factor));

      // Zoom toward the mouse position
      const point = canvas.getScenePoint(e);
      canvas.zoomToPoint(point, newZoom);
      if (marqueeRef.current.obj) {
        applyMarqueeStroke(canvas, marqueeRef.current.obj);
        for (const o of marqueeRef.current.extraObjs) applyMarqueeStroke(canvas, o);
      }
      canvas.renderAll();

      onZoomChangedRef.current?.(newZoom);
      updateViewportInfoRef.current();
    });

    // Move tool — drag empty canvas to pan the viewport (drag on object lets Fabric move it)
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'move') return;
      if (opt.e === undefined) return;
      if (canvas.findTarget(opt.e)) return;

      const point = clientPointFromEvent(opt.e);
      panRef.current = { isPanning: true, lastX: point.x, lastY: point.y };
      canvas.defaultCursor = 'grabbing';
      canvas.setCursor('grabbing');
    });

    canvas.on('mouse:move', (opt) => {
      if (!panRef.current.isPanning || !opt.e) return;
      const point = clientPointFromEvent(opt.e);
      const dx = point.x - panRef.current.lastX;
      const dy = point.y - panRef.current.lastY;
      canvas.relativePan(new fabric.Point(dx, dy));
      panRef.current.lastX = point.x;
      panRef.current.lastY = point.y;
      updateViewportInfoRef.current();
    });

    canvas.on('mouse:up', () => {
      if (panRef.current.isPanning) {
        panRef.current.isPanning = false;
        canvas.defaultCursor = 'grab';
      }
    });

    // Shape tool — click on empty canvas to drop a new shape at the click
    // position. Click on an existing shape lets Fabric handle it (select/drag).
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'shape') return;
      if (!opt.e) return;
      if (canvas.findTarget(opt.e)) return;
      const point = canvas.getScenePoint(opt.e);
      onShapeToolClickRef.current?.({ x: point.x, y: point.y });
    });

    // Text tool — click on empty canvas to drop a new IText at the click position.
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'text') return;
      if (!opt.e) return;
      if (canvas.findTarget(opt.e)) return;
      const point = canvas.getScenePoint(opt.e);
      onTextToolClickRef.current?.({ x: point.x, y: point.y });
    });

    // Eyedropper tool — click samples the pixel under the arrow tip and
    // promotes it to the brush/fill color. Same viewport-coord conversion as
    // the live mouse:move sampler.
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'eyedropper') return;
      if (!opt.e) return;
      const viewportPoint = canvas.getViewportPoint(opt.e);
      const dpr = canvas.getRetinaScaling?.() || 1;
      const px = Math.round(viewportPoint.x * dpr);
      const py = Math.round(viewportPoint.y * dpr);
      const lower = canvas.getContext();
      const w = lower.canvas.width;
      const h = lower.canvas.height;
      if (px < 0 || py < 0 || px >= w || py >= h) return;
      const pixel = lower.getImageData(px, py, 1, 1).data;
      const hex = `#${pixel[0].toString(16).padStart(2, '0')}${pixel[1].toString(16).padStart(2, '0')}${pixel[2].toString(16).padStart(2, '0')}`;
      onColorPickedRef.current?.(hex);
    });

    // Fill tool — click to flood-fill the clicked pixel color region with the
    // current brush color. The fill is rendered as a new image-shaped object
    // tagged with the active layerId, and clipped to the active marquee if one
    // is finalized. Tolerance/contiguous reuse the magic-wand settings.
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'fill') return;
      if (!opt.e) return;
      const bg = getBackgroundImage();
      if (!bg) return;
      const pointer = canvas.getScenePoint(opt.e);
      const px = Math.round(pointer.x);
      const py = Math.round(pointer.y);
      const bgW = bg.width || 0;
      const bgH = bg.height || 0;
      if (px < 0 || px >= bgW || py < 0 || py >= bgH) return;

      const imgEl = bg.getElement() as HTMLImageElement | HTMLCanvasElement;
      const sampler = document.createElement('canvas');
      sampler.width = bgW;
      sampler.height = bgH;
      const sCtx = sampler.getContext('2d');
      if (!sCtx) return;
      sCtx.drawImage(imgEl, 0, 0);
      const imgData = sCtx.getImageData(0, 0, bgW, bgH);

      const mask = computeWandMask(
        imgData,
        px,
        py,
        wandToleranceRef.current,
        wandContiguousRef.current,
      );

      // If a finalized marquee is active, constrain the fill: the click must
      // land inside the marquee, and the wand mask is intersected with the
      // marquee mask so only pixels inside the marquee are painted. This
      // applies regardless of the marquee shape (rect, ellipse, lasso,
      // polygon, wand).
      const fillMarquee = marqueeRef.current;
      if (fillMarquee.obj && !fillMarquee.isDrawing) {
        const marqueeMask = buildMarqueeMask(fillMarquee, bgW, bgH);
        if (marqueeMask) {
          const clickIdx = py * bgW + px;
          if (!marqueeMask[clickIdx]) return;
          for (let i = 0; i < mask.length; i++) {
            if (!marqueeMask[i]) mask[i] = 0;
          }
        }
      }

      // Render the fill into an offscreen canvas: brushColor where mask=1,
      // transparent elsewhere.
      const fillCanvas = document.createElement('canvas');
      fillCanvas.width = bgW;
      fillCanvas.height = bgH;
      const fCtx = fillCanvas.getContext('2d');
      if (!fCtx) return;
      const color = brushColorRef.current;
      const r = parseInt(color.slice(1, 3), 16) || 0;
      const g = parseInt(color.slice(3, 5), 16) || 0;
      const b = parseInt(color.slice(5, 7), 16) || 0;
      const fillData = fCtx.createImageData(bgW, bgH);
      let painted = 0;
      for (let i = 0; i < mask.length; i++) {
        if (mask[i]) {
          const di = i * 4;
          fillData.data[di] = r;
          fillData.data[di + 1] = g;
          fillData.data[di + 2] = b;
          fillData.data[di + 3] = 255;
          painted++;
        }
      }
      if (painted === 0) return;
      fCtx.putImageData(fillData, 0, 0);

      const fillImg = new fabric.FabricImage(fillCanvas, {
        left: 0,
        top: 0,
        scaleX: 1,
        scaleY: 1,
        selectable: true,
        evented: true,
      });
      (fillImg as fabric.FabricImage & { id: string }).id = `fill_${Date.now()}`;

      const layerId = activeLayerIdRef.current;
      if (layerId) setObjectLayerId(fillImg, layerId);

      canvas.add(fillImg);
      canvas.renderAll();
      onObjectModifiedRef.current?.();
    });

    // Marquee selection — supports rect, ellipse, lasso, and polygon shapes.
    // Rect/ellipse: drag to size. Lasso: drag to capture freeform path.
    // Polygon: each click adds a vertex; close by clicking near the first point or double-clicking.
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'marquee') return;
      if (opt.e === undefined) return;
      if (canvas.findTarget(opt.e)) return;

      const pointer = canvas.getScenePoint(opt.e);
      const m = marqueeRef.current;

      if (m.shape === 'rect' || m.shape === 'ellipse') {
        if (m.obj) canvas.remove(m.obj);
        const obj =
          m.shape === 'rect'
            ? new fabric.Rect({
                ...MARQUEE_STYLE,
                left: pointer.x,
                top: pointer.y,
                width: 0,
                height: 0,
              })
            : new fabric.Ellipse({
                ...MARQUEE_STYLE,
                left: pointer.x,
                top: pointer.y,
                rx: 0,
                ry: 0,
              });
        tagAsMarquee(obj);
        applyMarqueeColor(obj, marqueeColorRef.current);
        applyMarqueeStroke(canvas, obj);
        canvas.add(obj);
        m.obj = obj;
        m.startX = pointer.x;
        m.startY = pointer.y;
        m.points = [];
        m.isDrawing = true;
      } else if (m.shape === 'lasso') {
        if (m.obj) canvas.remove(m.obj);
        m.points = [{ x: pointer.x, y: pointer.y }];
        const poly = new fabric.Polyline(m.points, MARQUEE_STYLE);
        tagAsMarquee(poly);
        applyMarqueeColor(poly, marqueeColorRef.current);
        applyMarqueeStroke(canvas, poly);
        canvas.add(poly);
        m.obj = poly;
        m.isDrawing = true;
      } else if (m.shape === 'polygon') {
        if (!m.isDrawing) {
          if (m.obj) canvas.remove(m.obj);
          m.points = [{ x: pointer.x, y: pointer.y }];
          const poly = new fabric.Polyline(m.points, MARQUEE_STYLE);
          tagAsMarquee(poly);
          applyMarqueeColor(poly, marqueeColorRef.current);
          applyMarqueeStroke(canvas, poly);
          canvas.add(poly);
          m.obj = poly;
          m.isDrawing = true;
        } else {
          const first = m.points[0];
          const dx = pointer.x - first.x;
          const dy = pointer.y - first.y;
          const threshold = POLYGON_CLOSE_THRESHOLD_PX / canvas.getZoom();
          if (m.points.length >= 3 && dx * dx + dy * dy < threshold * threshold) {
            closePolygonMarquee(canvas, m, marqueeColorRef.current);
          } else {
            m.points = [...m.points, { x: pointer.x, y: pointer.y }];
            replaceMarqueePolyline(canvas, m, marqueeColorRef.current);
          }
        }
      } else if (m.shape === 'wand') {
        const bg = getBackgroundImage();
        if (!bg) {
          canvas.renderAll();
          return;
        }
        const imgEl = bg.getElement() as HTMLImageElement | HTMLCanvasElement;
        const bgW = bg.width || 0;
        const bgH = bg.height || 0;
        const px = Math.round(pointer.x);
        const py = Math.round(pointer.y);
        if (px < 0 || px >= bgW || py < 0 || py >= bgH) {
          canvas.renderAll();
          return;
        }

        // Clear any prior wand selection first.
        if (m.obj) canvas.remove(m.obj);
        for (const o of m.extraObjs) canvas.remove(o);
        m.obj = null;
        m.extraObjs = [];
        m.points = [];
        m.wandMaskCanvas = null;

        // Sample image pixels and compute the mask.
        const offscreen = document.createElement('canvas');
        offscreen.width = bgW;
        offscreen.height = bgH;
        const ctx = offscreen.getContext('2d');
        if (!ctx) {
          canvas.renderAll();
          return;
        }
        ctx.drawImage(imgEl, 0, 0);
        const imgData = ctx.getImageData(0, 0, bgW, bgH);
        const tolerance = wandToleranceRef.current;
        const contiguous = wandContiguousRef.current;
        const mask = computeWandMask(imgData, px, py, tolerance, contiguous);

        // Cache the mask as an opaque-where-selected canvas for the eraser path.
        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = bgW;
        maskCanvas.height = bgH;
        const maskCtx = maskCanvas.getContext('2d');
        if (maskCtx) {
          const maskData = maskCtx.createImageData(bgW, bgH);
          for (let i = 0; i < mask.length; i++) {
            if (mask[i]) {
              const di = i * 4;
              maskData.data[di] = 255;
              maskData.data[di + 1] = 255;
              maskData.data[di + 2] = 255;
              maskData.data[di + 3] = 255;
            }
          }
          maskCtx.putImageData(maskData, 0, 0);
        }
        m.wandMaskCanvas = maskCanvas;
        m.wandTolerance = tolerance;
        m.wandContiguous = contiguous;

        // Trace contours into Fabric Polygons.
        const contours = traceContours(mask, bgW, bgH);
        const polys: fabric.Polygon[] = [];
        for (const contour of contours) {
          const poly = new fabric.Polygon(contour, MARQUEE_STYLE);
          tagAsMarquee(poly);
          applyMarqueeColor(poly, marqueeColorRef.current);
          applyMarqueeStroke(canvas, poly);
          canvas.add(poly);
          polys.push(poly);
        }
        if (polys.length > 0) {
          m.obj = polys[0];
          m.extraObjs = polys.slice(1);
        }
        m.isDrawing = false;
      }
      canvas.renderAll();
      refreshMarqueeSelectionState();
    });

    canvas.on('mouse:move', (opt) => {
      const m = marqueeRef.current;
      if (!m.isDrawing || !m.obj || !opt.e) return;
      if (m.shape === 'polygon') return; // polygon advances on click, not move

      const pointer = canvas.getScenePoint(opt.e);

      const shiftHeld = !!(opt.e as MouseEvent).shiftKey;
      const constrainSquare = forceSquareRef.current || shiftHeld;

      if (m.shape === 'rect') {
        const dx = pointer.x - m.startX;
        const dy = pointer.y - m.startY;
        let width: number;
        let height: number;
        let left: number;
        let top: number;
        if (constrainSquare) {
          const size = Math.max(Math.abs(dx), Math.abs(dy));
          width = size;
          height = size;
          left = dx >= 0 ? m.startX : m.startX - size;
          top = dy >= 0 ? m.startY : m.startY - size;
        } else {
          width = Math.abs(dx);
          height = Math.abs(dy);
          left = Math.min(m.startX, pointer.x);
          top = Math.min(m.startY, pointer.y);
        }
        (m.obj as fabric.Rect).set({ left, top, width, height });
      } else if (m.shape === 'ellipse') {
        const dx = pointer.x - m.startX;
        const dy = pointer.y - m.startY;
        let w: number;
        let h: number;
        let left: number;
        let top: number;
        if (constrainSquare) {
          const size = Math.max(Math.abs(dx), Math.abs(dy));
          w = size;
          h = size;
          left = dx >= 0 ? m.startX : m.startX - size;
          top = dy >= 0 ? m.startY : m.startY - size;
        } else {
          w = Math.abs(dx);
          h = Math.abs(dy);
          left = Math.min(m.startX, pointer.x);
          top = Math.min(m.startY, pointer.y);
        }
        (m.obj as fabric.Ellipse).set({ left, top, rx: w / 2, ry: h / 2 });
      } else if (m.shape === 'lasso') {
        m.points = [...m.points, { x: pointer.x, y: pointer.y }];
        replaceMarqueePolyline(canvas, m, marqueeColorRef.current);
      }
      canvas.renderAll();
    });

    canvas.on('mouse:up', () => {
      const m = marqueeRef.current;
      if (!m.isDrawing || !m.obj) return;
      if (m.shape === 'polygon') return; // polygon stays open until explicit close

      if (m.shape === 'rect' || m.shape === 'ellipse') {
        m.isDrawing = false;
        const w =
          m.shape === 'rect'
            ? (m.obj as fabric.Rect).width || 0
            : ((m.obj as fabric.Ellipse).rx || 0) * 2;
        const h =
          m.shape === 'rect'
            ? (m.obj as fabric.Rect).height || 0
            : ((m.obj as fabric.Ellipse).ry || 0) * 2;
        if (w < 3 && h < 3) {
          canvas.remove(m.obj);
          m.obj = null;
        }
      } else if (m.shape === 'lasso') {
        m.isDrawing = false;
        if (m.points.length < 3) {
          canvas.remove(m.obj);
          m.obj = null;
          m.points = [];
        } else {
          canvas.remove(m.obj);
          const closed = new fabric.Polygon(m.points, MARQUEE_STYLE);
          tagAsMarquee(closed);
          applyMarqueeColor(closed, marqueeColorRef.current);
          applyMarqueeStroke(canvas, closed);
          canvas.add(closed);
          m.obj = closed;
        }
      }
      canvas.renderAll();
      refreshMarqueeSelectionState();
    });

    canvas.on('mouse:dblclick', () => {
      if (activeToolRef.current !== 'marquee') return;
      const m = marqueeRef.current;
      if (m.shape !== 'polygon' || !m.isDrawing) return;
      // The browser fires two mousedowns before dblclick, so the second
      // mousedown already added a vertex at the dblclick position. Pop it
      // so the closing edge runs to the previous vertex, not back to itself.
      if (m.points.length >= 2) {
        m.points = m.points.slice(0, -1);
      }
      if (m.points.length < 3) return;
      closePolygonMarquee(canvas, m, marqueeColorRef.current);
      canvas.renderAll();
      refreshMarqueeSelectionState();
    });

    // Two-finger pinch zoom on touch devices. Bound to the upper canvas so we get
    // raw touch events with full multi-touch info (Fabric's mouse:* events only
    // surface the first finger). passive:false lets us preventDefault to suppress
    // the browser's native pinch-zoom of the page.
    const upperEl = canvas.upperCanvasEl as HTMLCanvasElement | undefined;
    const touchDistance = (e: TouchEvent) => {
      const a = e.touches[0];
      const b = e.touches[1];
      return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
    };
    const touchMidpoint = (e: TouchEvent, el: HTMLElement) => {
      const a = e.touches[0];
      const b = e.touches[1];
      const rect = el.getBoundingClientRect();
      return new fabric.Point(
        (a.clientX + b.clientX) / 2 - rect.left,
        (a.clientY + b.clientY) / 2 - rect.top,
      );
    };
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 2 || !upperEl) return;
      e.preventDefault();
      const vt = canvas.viewportTransform;
      pinchRef.current = {
        isPinching: true,
        startDist: touchDistance(e),
        startZoom: vt ? vt[0] : 1,
      };
      panRef.current.isPanning = false;
      // Cancel any in-progress / finalized marquee — including wand contours
      // (extraObjs) and the cached wand mask, which the marquee tool tracks
      // alongside the primary object.
      const m = marqueeRef.current;
      if (m.obj || m.extraObjs.length > 0) {
        if (m.obj) canvas.remove(m.obj);
        for (const o of m.extraObjs) canvas.remove(o);
        m.obj = null;
        m.extraObjs = [];
        m.points = [];
        m.wandMaskCanvas = null;
        m.isDrawing = false;
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!pinchRef.current.isPinching || e.touches.length !== 2 || !upperEl) return;
      e.preventDefault();
      const newDist = touchDistance(e);
      if (pinchRef.current.startDist === 0) return;
      const factor = newDist / pinchRef.current.startDist;
      const newZoom = Math.max(0.1, Math.min(10, pinchRef.current.startZoom * factor));
      canvas.zoomToPoint(touchMidpoint(e, upperEl), newZoom);
      if (marqueeRef.current.obj) {
        applyMarqueeStroke(canvas, marqueeRef.current.obj);
        for (const o of marqueeRef.current.extraObjs) applyMarqueeStroke(canvas, o);
      }
      canvas.renderAll();
      onZoomChangedRef.current?.(newZoom);
      updateViewportInfoRef.current();
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) {
        pinchRef.current.isPinching = false;
      }
    };
    if (upperEl) {
      upperEl.addEventListener('touchstart', onTouchStart, { passive: false });
      upperEl.addEventListener('touchmove', onTouchMove, { passive: false });
      upperEl.addEventListener('touchend', onTouchEnd);
      upperEl.addEventListener('touchcancel', onTouchEnd);
    }

    return () => {
      observer.disconnect();
      if (upperEl) {
        upperEl.removeEventListener('touchstart', onTouchStart);
        upperEl.removeEventListener('touchmove', onTouchMove);
        upperEl.removeEventListener('touchend', onTouchEnd);
        upperEl.removeEventListener('touchcancel', onTouchEnd);
      }
      canvas.dispose();
      canvasRef.current = null;
    };
  }, []);

  /** Zoom and pan the viewport so the image fits centered in the wrapper */
  const zoomToFit = useCallback(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return 1;

    const vpW = wrapper.clientWidth;
    const vpH = wrapper.clientHeight;
    const imgW = imageDimsRef.current.width;
    const imgH = imageDimsRef.current.height;
    if (!imgW || !imgH || !vpW || !vpH) return 1;

    // Ensure canvas element matches wrapper
    canvas.setDimensions({ width: vpW, height: vpH });

    const padding = 40;
    const zoom = Math.min((vpW - padding * 2) / imgW, (vpH - padding * 2) / imgH, 1);

    const panX = (vpW - imgW * zoom) / 2;
    const panY = (vpH - imgH * zoom) / 2;

    canvas.setViewportTransform([zoom, 0, 0, zoom, panX, panY]);
    if (marqueeRef.current.obj) {
      applyMarqueeStroke(canvas, marqueeRef.current.obj);
      for (const o of marqueeRef.current.extraObjs) applyMarqueeStroke(canvas, o);
    }
    canvas.renderAll();
    updateViewportInfo();
    return zoom;
  }, [wrapperRef, updateViewportInfo]);

  /** Load an image onto the canvas. Returns the fit zoom level. */
  const loadImage = useCallback(
    async (src: string): Promise<number> => {
      const canvas = canvasRef.current;
      if (!canvas) return 1;

      const img = await fabric.FabricImage.fromURL(src, { crossOrigin: 'anonymous' });
      backgroundImageRef.current = img;
      tagAsBackground(img);

      const imgW = img.width || 1;
      const imgH = img.height || 1;
      imageDimsRef.current = { width: imgW, height: imgH };

      // Replace the loaded HTMLImageElement with a same-sized canvas so the
      // bg becomes a paintable raster surface.
      const bgCanvas = ensureCanvasElement(
        img.getElement() as HTMLImageElement | HTMLCanvasElement,
        imgW,
        imgH,
      );
      img.setElement(bgCanvas);

      img.set({
        left: 0,
        top: 0,
        scaleX: 1,
        scaleY: 1,
        selectable: false,
        evented: false,
        erasable: false,
        // Disable Fabric's per-object cache so live edits to the underlying
        // canvas show up on the next render without manual invalidation.
        objectCaching: false,
      } as Partial<fabric.FabricImage>);

      // The paint-surface map is keyed by layerId, which the caller sets via
      // `setBackgroundLayerId`. Reset any prior entries here; the new bg will
      // be registered when its layerId is assigned.
      paintSurfacesRef.current.clear();

      canvas.clear();
      canvas.add(img);
      canvas.sendObjectToBack(img);

      // Wait for layout to settle, then fit
      await new Promise((r) => requestAnimationFrame(r));
      const zoom = zoomToFit();

      onCanvasDimensions?.(imgW, imgH);
      return zoom;
    },
    [onCanvasDimensions, zoomToFit],
  );

  /** Combine a hex color and 0–1 opacity into a Fabric-friendly rgba string. */
  const composeBrushColor = (hex: string, opacity: number): string => {
    const r = parseInt(hex.slice(1, 3), 16) || 0;
    const g = parseInt(hex.slice(3, 5), 16) || 0;
    const b = parseInt(hex.slice(5, 7), 16) || 0;
    const a = Math.max(0, Math.min(1, opacity));
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };

  /** Enable free drawing mode */
  const enableDrawMode = useCallback((settings: BrushSettings) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.isDrawingMode = true;
    const brush = new fabric.PencilBrush(canvas);
    brush.color = composeBrushColor(settings.color, settings.opacity);
    brush.width = settings.width;
    canvas.freeDrawingBrush = brush;
  }, []);

  /**
   * Mutate the active free-drawing brush's color/width/opacity in place. Use
   * this on settings changes while the Draw tool is active so the next stroke
   * picks up the new values without re-toggling tools. For Eraser, only the
   * width is updated (the color is hard-coded for destination-out semantics).
   */
  const updateBrush = useCallback((settings: BrushSettings) => {
    // Always mirror opacity into the eraser ref — even when the active tool
    // is Draw, the user might switch to Eraser later expecting the slider
    // they just set to apply.
    eraserOpacityRef.current = settings.opacity;
    const canvas = canvasRef.current;
    if (!canvas || !canvas.freeDrawingBrush) return;
    if (activeToolRef.current === 'draw') {
      canvas.freeDrawingBrush.color = composeBrushColor(settings.color, settings.opacity);
    }
    if (activeToolRef.current === 'draw' || activeToolRef.current === 'eraser') {
      canvas.freeDrawingBrush.width = settings.width;
    }
  }, []);

  /** Disable drawing mode */
  const disableDrawMode = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.isDrawingMode = false;
  }, []);

  /** Add a text object to the canvas center (in image coordinates) */
  const addText = useCallback(
    (
      text: string,
      settings: TextSettings,
      layerId?: string,
      position?: { x: number; y: number },
    ) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;

      const textObj = new fabric.IText(text, {
        left: position ? position.x : imgW / 2,
        top: position ? position.y : imgH / 2,
        fontFamily: settings.fontFamily,
        fontSize: settings.fontSize,
        fontWeight: settings.fontWeight,
        fontStyle: settings.fontStyle,
        underline: settings.underline,
        textAlign: settings.textAlign,
        fill: settings.fill,
        originX: 'center',
        originY: 'center',
      });

      (textObj as fabric.IText & { id: string }).id = `text_${Date.now()}`;
      if (layerId) setObjectLayerId(textObj, layerId);
      canvas.add(textObj);
      canvas.setActiveObject(textObj);
      // Stash the arch slider value on the object for later round-trip; build
      // the path now that Fabric has measured the text's width.
      (textObj as fabric.IText & { archValue: number }).archValue = settings.arch;
      if (settings.arch !== 0) {
        const path = buildTextArchPath(textObj.width || 0, settings.arch);
        if (path) textObj.set({ path });
      }
      // Drop straight into edit mode with the placeholder text selected so
      // the user can type to replace it.
      textObj.enterEditing();
      textObj.selectAll();
      canvas.renderAll();
      // Snapshot is the caller's responsibility (see addShape comment).
    },
    [],
  );

  /** Apply TextSettings to the active IText. No-op if active isn't an IText. */
  const updateActiveText = useCallback((settings: TextSettings) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const active = canvas.getActiveObject();
    if (!(active instanceof fabric.IText)) return;
    active.set({
      fontFamily: settings.fontFamily,
      fontSize: settings.fontSize,
      fontWeight: settings.fontWeight,
      fontStyle: settings.fontStyle,
      underline: settings.underline,
      textAlign: settings.textAlign,
      fill: settings.fill,
    });
    // Update arch path. We re-measure width using a temp Text without a path
    // so the arch geometry tracks the natural text width even if the previous
    // path was already shaping the text.
    (active as fabric.IText & { archValue: number }).archValue = settings.arch;
    if (settings.arch === 0) {
      active.set({ path: undefined });
    } else {
      const measureText = new fabric.Text(active.text || '', {
        fontFamily: settings.fontFamily,
        fontSize: settings.fontSize,
        fontWeight: settings.fontWeight,
        fontStyle: settings.fontStyle,
      });
      const naturalWidth = measureText.width || active.width || 0;
      const path = buildTextArchPath(naturalWidth, settings.arch);
      if (path) active.set({ path });
    }
    active.setCoords();
    canvas.requestRenderAll();
  }, []);

  /** Read the active IText's properties as a TextSettings snapshot. */
  const getActiveTextSettings = useCallback((): TextSettings | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const active = canvas.getActiveObject();
    if (!(active instanceof fabric.IText)) return null;
    const fontWeightRaw = active.fontWeight;
    const fontWeight: 'normal' | 'bold' =
      fontWeightRaw === 'bold' || fontWeightRaw === 700 ? 'bold' : 'normal';
    const fontStyleRaw = active.fontStyle;
    const fontStyle: 'normal' | 'italic' = fontStyleRaw === 'italic' ? 'italic' : 'normal';
    const textAlignRaw = active.textAlign;
    const textAlign: 'left' | 'center' | 'right' =
      textAlignRaw === 'center' || textAlignRaw === 'right' ? textAlignRaw : 'left';
    const archValue = (active as fabric.IText & { archValue?: number }).archValue ?? 0;
    return {
      fontFamily: active.fontFamily ?? 'Arial',
      fontSize: active.fontSize ?? 24,
      fontWeight,
      fontStyle,
      underline: active.underline ?? false,
      textAlign,
      fill: typeof active.fill === 'string' ? active.fill : '#000000',
      arch: archValue,
    };
  }, []);

  /** Add a shape at the canvas center (in image coordinates), or at a given
   *  scene-space `position` if provided. */
  const addShape = useCallback(
    (
      shapeType: ShapeType,
      settings: ShapeSettings,
      layerId?: string,
      position?: { x: number; y: number },
    ) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;
      const center = position
        ? { left: position.x, top: position.y }
        : { left: imgW / 2, top: imgH / 2 };
      let shape: fabric.FabricObject;

      switch (shapeType) {
        case 'rect':
          shape = new fabric.Rect({
            ...center,
            width: 150,
            height: 100,
            fill: settings.fill,
            stroke: settings.stroke,
            strokeWidth: settings.strokeWidth,
            rx: settings.cornerRadius,
            ry: settings.cornerRadius,
            originX: 'center',
            originY: 'center',
          });
          break;
        case 'circle':
          shape = new fabric.Circle({
            ...center,
            radius: 60,
            fill: settings.fill,
            stroke: settings.stroke,
            strokeWidth: settings.strokeWidth,
            originX: 'center',
            originY: 'center',
          });
          break;
        case 'triangle':
          shape = new fabric.Triangle({
            ...center,
            width: 120,
            height: 120,
            fill: settings.fill,
            stroke: settings.stroke,
            strokeWidth: settings.strokeWidth,
            originX: 'center',
            originY: 'center',
          });
          break;
        case 'line':
          shape = new fabric.Line([center.left - 75, center.top, center.left + 75, center.top], {
            stroke: settings.stroke,
            strokeWidth: settings.strokeWidth,
          });
          break;
        default:
          return;
      }

      (shape as fabric.FabricObject & { id: string }).id = `shape_${Date.now()}`;
      if (layerId) setObjectLayerId(shape, layerId);
      canvas.add(shape);
      canvas.setActiveObject(shape);
      canvas.renderAll();
      // Snapshot is the caller's responsibility (see addText comment).
    },
    [],
  );

  /**
   * Live-update the active shape with new shape settings. If the type matches,
   * mutate fill/stroke/strokeWidth/cornerRadius in place. If the type changed,
   * replace the active shape with a fresh one at the same position +
   * layerId, then make the new shape active. No-op if the active object isn't
   * a shape (or there's no active object).
   */
  const updateActiveShape = useCallback((settings: ShapeSettings) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const active = canvas.getActiveObject();
    if (!active) return;

    const currentType: ShapeType | null =
      active instanceof fabric.Rect
        ? 'rect'
        : active instanceof fabric.Circle
          ? 'circle'
          : active instanceof fabric.Triangle
            ? 'triangle'
            : active instanceof fabric.Line
              ? 'line'
              : null;
    if (!currentType) return;

    if (currentType === settings.type) {
      const updates: Partial<fabric.FabricObjectProps> = {
        fill: settings.fill,
        stroke: settings.stroke,
        strokeWidth: settings.strokeWidth,
      };
      active.set(updates);
      if (active instanceof fabric.Rect) {
        active.set({ rx: settings.cornerRadius, ry: settings.cornerRadius });
      }
      active.setCoords();
      canvas.requestRenderAll();
      return;
    }

    // Type changed — recreate at the same position and re-tag with the same
    // layerId so the layer association is preserved.
    const layerId = getObjectLayerId(active);
    const left = active.left;
    const top = active.top;
    const originX = active.originX;
    const originY = active.originY;

    let next: fabric.FabricObject;
    switch (settings.type) {
      case 'rect':
        next = new fabric.Rect({
          left,
          top,
          originX,
          originY,
          width: 150,
          height: 100,
          fill: settings.fill,
          stroke: settings.stroke,
          strokeWidth: settings.strokeWidth,
          rx: settings.cornerRadius,
          ry: settings.cornerRadius,
        });
        break;
      case 'circle':
        next = new fabric.Circle({
          left,
          top,
          originX,
          originY,
          radius: 60,
          fill: settings.fill,
          stroke: settings.stroke,
          strokeWidth: settings.strokeWidth,
        });
        break;
      case 'triangle':
        next = new fabric.Triangle({
          left,
          top,
          originX,
          originY,
          width: 120,
          height: 120,
          fill: settings.fill,
          stroke: settings.stroke,
          strokeWidth: settings.strokeWidth,
        });
        break;
      case 'line': {
        const cx = left ?? 0;
        const cy = top ?? 0;
        next = new fabric.Line([cx - 75, cy, cx + 75, cy], {
          stroke: settings.stroke,
          strokeWidth: settings.strokeWidth,
        });
        break;
      }
      default:
        return;
    }

    canvas.remove(active);
    canvas.discardActiveObject();
    (next as fabric.FabricObject & { id: string }).id = `shape_${Date.now()}`;
    if (layerId) setObjectLayerId(next, layerId);
    canvas.add(next);
    canvas.setActiveObject(next);
    canvas.requestRenderAll();
  }, []);

  /**
   * Read the active shape's properties as a `ShapeSettings` snapshot. Used to
   * mirror canvas selection state into the Shape panel so changing the panel
   * sliders edits the *currently selected* shape, not whatever was last
   * touched. Returns null if the active object isn't a shape (or absent).
   */
  const getActiveShapeSettings = useCallback((): ShapeSettings | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const active = canvas.getActiveObject();
    if (!active) return null;

    let type: ShapeType;
    let cornerRadius = 0;
    if (active instanceof fabric.Rect) {
      type = 'rect';
      cornerRadius = (active as fabric.Rect).rx ?? 0;
    } else if (active instanceof fabric.Circle) {
      type = 'circle';
    } else if (active instanceof fabric.Triangle) {
      type = 'triangle';
    } else if (active instanceof fabric.Line) {
      type = 'line';
    } else {
      return null;
    }

    const fill = typeof active.fill === 'string' ? active.fill : 'transparent';
    const stroke = typeof active.stroke === 'string' ? active.stroke : '#000000';
    return {
      type,
      fill,
      stroke,
      strokeWidth: active.strokeWidth ?? 0,
      cornerRadius,
    };
  }, []);

  /**
   * Destructively convert the background image to grayscale by rewriting
   * its underlying pixels with the Rec. 601 luminance of each pixel
   * (`0.299·R + 0.587·G + 0.114·B`). User-adjustable filters (brightness,
   * contrast, saturation) continue to apply on top of the now-grayscale
   * pixels. If the bg is registered as a paint surface, the surface map
   * is updated to point at the new canvas so subsequent strokes draw on
   * the same pixels we render from. Returns false if there's no bg image.
   */
  const convertToGrayscale = useCallback((): boolean => {
    const canvas = canvasRef.current;
    const img = getBackgroundImage();
    if (!canvas || !img) return false;
    const w = img.width ?? 0;
    const h = img.height ?? 0;
    if (w <= 0 || h <= 0) return false;

    const off = document.createElement('canvas');
    off.width = w;
    off.height = h;
    const ctx = off.getContext('2d');
    if (!ctx) return false;
    ctx.drawImage(img.getElement() as HTMLImageElement | HTMLCanvasElement, 0, 0, w, h);

    const data = ctx.getImageData(0, 0, w, h);
    const px = data.data;
    for (let i = 0; i < px.length; i += 4) {
      const lum = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
      px[i] = lum;
      px[i + 1] = lum;
      px[i + 2] = lum;
    }
    ctx.putImageData(data, 0, 0);

    img.setElement(off);
    const bgLayerId = getObjectLayerId(img);
    if (bgLayerId) {
      paintSurfacesRef.current.set(bgLayerId, { canvas: off, fabricImg: img });
    }
    img.dirty = true;
    // Re-run any active filters on the new (grayscale) source.
    if (img.filters && img.filters.length > 0) img.applyFilters();
    img.setCoords();
    canvas.renderAll();
    // Fire onObjectModified so callers that don't manually wire dirty +
    // history (e.g. future direct callers) get the same behavior as
    // EditorPage's handleConvertGrayscale.
    onObjectModifiedRef.current?.();
    return true;
  }, [getBackgroundImage]);

  /** Apply filters to the background image */
  const applyFilters = useCallback(
    (settings: FilterSettings) => {
      const img = getBackgroundImage();
      const canvas = canvasRef.current;
      if (!img || !canvas) return;

      img.filters = [];

      if (settings.brightness !== 0) {
        img.filters.push(new fabric.filters.Brightness({ brightness: settings.brightness / 100 }));
      }
      if (settings.contrast !== 0) {
        img.filters.push(new fabric.filters.Contrast({ contrast: settings.contrast / 100 }));
      }
      if (settings.saturation !== 0) {
        img.filters.push(new fabric.filters.Saturation({ saturation: settings.saturation / 100 }));
      }

      img.applyFilters();
      canvas.renderAll();
    },
    [getBackgroundImage],
  );

  /** Delete pixels within marquee selection, or remove selected objects */
  const deleteSelected = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const m = marqueeRef.current;

    // If there's a finalized marquee selection, clip-clear pixels inside it
    // on the *active layer's* paint surface only, and remove any non-paint
    // overlay objects on the active layer whose bounding box overlaps. This
    // matches Photoshop's behavior — marquee delete operates on the active
    // layer, not every layer.
    if (m.obj && !m.isDrawing) {
      const isWand = m.shape === 'wand' && !!m.wandMaskCanvas;
      const wandMask = m.wandMaskCanvas;
      const activeLayerId = activeLayerIdRef.current;

      const clipClearSurface = (surface: {
        canvas: HTMLCanvasElement;
        fabricImg: fabric.FabricImage;
      }) => {
        const ctx = surface.canvas.getContext('2d');
        if (!ctx) return;
        // Marquee/wand geometry is in scene coords. If the layer's paint
        // surface has been moved (left/top != 0), translate the context so
        // scene coords map to the right surface pixels.
        const offsetX = -(surface.fabricImg.left ?? 0);
        const offsetY = -(surface.fabricImg.top ?? 0);
        if (isWand && wandMask) {
          ctx.save();
          ctx.translate(offsetX, offsetY);
          ctx.globalCompositeOperation = 'destination-out';
          ctx.drawImage(wandMask, 0, 0);
          ctx.restore();
          return;
        }
        ctx.save();
        ctx.translate(offsetX, offsetY);
        const clipped = applyMarqueeClipToContext(ctx, m);
        if (clipped) ctx.clearRect(0, 0, surface.canvas.width, surface.canvas.height);
        ctx.restore();
      };

      // Find vector overlays (shape/text/pasted-image) on the active layer
      // whose bbox intersects the marquee. These will be rasterized onto the
      // layer's paint surface so the subsequent clip-clear cuts a hole
      // through them — matching Photoshop's "marquee delete cuts through
      // everything on the active layer" behavior.
      const marqueeBounds = (() => {
        if (!m.obj) return null;
        let b = m.obj.getBoundingRect();
        for (const extra of m.extraObjs) {
          const eb = extra.getBoundingRect();
          const left = Math.min(b.left, eb.left);
          const top = Math.min(b.top, eb.top);
          const right = Math.max(b.left + b.width, eb.left + eb.width);
          const bottom = Math.max(b.top + b.height, eb.top + eb.height);
          b = { left, top, width: right - left, height: bottom - top };
        }
        return b;
      })();
      const paintFabricImgs = new Set<fabric.Object>();
      for (const s of paintSurfacesRef.current.values()) paintFabricImgs.add(s.fabricImg);
      const overlaysToRasterize: fabric.Object[] = [];
      if (activeLayerId && marqueeBounds && marqueeBounds.width > 0 && marqueeBounds.height > 0) {
        const mLeft = marqueeBounds.left;
        const mTop = marqueeBounds.top;
        const mRight = mLeft + marqueeBounds.width;
        const mBottom = mTop + marqueeBounds.height;
        // Iterate canvas objects in z-order (back-to-front) so rasterization
        // preserves stacking order on the paint surface.
        for (const o of canvas.getObjects()) {
          if (paintFabricImgs.has(o)) continue;
          if (isMarqueeObject(o)) continue;
          if ((o as unknown as { _isCropRect?: boolean })._isCropRect) continue;
          if (o === m.obj || m.extraObjs.includes(o)) continue;
          if (getObjectLayerId(o) !== activeLayerId) continue;
          const ob = o.getBoundingRect();
          const intersects =
            ob.left < mRight &&
            ob.left + ob.width > mLeft &&
            ob.top < mBottom &&
            ob.top + ob.height > mTop;
          if (intersects) overlaysToRasterize.push(o);
        }
      }

      // Rasterize intersecting overlays onto the active layer's paint surface,
      // then remove their vector representations from the main canvas. The
      // subsequent clip-clear will erase the marquee region from the surface,
      // including these freshly-rasterized pixels.
      if (activeLayerId && overlaysToRasterize.length > 0) {
        const surface = ensurePaintSurfaceRef.current?.(activeLayerId) ?? null;
        if (surface) {
          const ctx = surface.canvas.getContext('2d');
          if (ctx) {
            const w = surface.canvas.width;
            const h = surface.canvas.height;
            const tempEl = document.createElement('canvas');
            tempEl.width = w;
            tempEl.height = h;
            const tempCanvas = new fabric.StaticCanvas(tempEl, {
              width: w,
              height: h,
              enableRetinaScaling: false,
              renderOnAddRemove: false,
              backgroundColor: 'transparent',
            });
            for (const o of overlaysToRasterize) {
              canvas.remove(o);
              tempCanvas.add(o);
            }
            tempCanvas.renderAll();
            const offsetX = -(surface.fabricImg.left ?? 0);
            const offsetY = -(surface.fabricImg.top ?? 0);
            ctx.save();
            ctx.translate(offsetX, offsetY);
            ctx.drawImage(tempEl, 0, 0);
            ctx.restore();
            tempCanvas.dispose();
            surface.fabricImg.setElement(surface.canvas);
            surface.fabricImg.dirty = true;
            surface.fabricImg.setCoords();
          }
        }
      }

      // Apply clip-clear to the active layer's paint surface only — now
      // containing both pre-existing strokes and any just-rasterized overlay
      // pixels.
      if (activeLayerId) {
        const activeSurface = paintSurfacesRef.current.get(activeLayerId);
        if (activeSurface) {
          clipClearSurface(activeSurface);
          // Force Fabric to re-bind via `setElement` — `dirty = true` alone
          // doesn't propagate canvas mutations reliably.
          activeSurface.fabricImg.setElement(activeSurface.canvas);
          activeSurface.fabricImg.dirty = true;
          activeSurface.fabricImg.setCoords();
        }
      }

      // If the active layer is the Background and bg has filters, re-run them
      // so the filter pipeline picks up the cleared pixels. `setElement`
      // resets `_element` to the (un-filtered) canvas; `applyFilters` rebuilds
      // `_filteredEl` from that.
      const bg = getBackgroundImage();
      if (bg && activeLayerId === getObjectLayerId(bg) && bg.filters && bg.filters.length > 0) {
        bg.applyFilters();
      }

      canvas.discardActiveObject();
      canvas.remove(m.obj);
      for (const o of m.extraObjs) canvas.remove(o);
      m.obj = null;
      m.extraObjs = [];
      m.points = [];
      m.wandMaskCanvas = null;
      setHasMarqueeSelection(false);
      canvas.renderAll();
      onObjectModified?.();
      return;
    }

    // Otherwise, delete selected Fabric objects
    const active = canvas.getActiveObjects();
    if (active.length === 0) return;
    const bg = getBackgroundImage();
    active.forEach((obj) => {
      if (obj === bg) return;
      canvas.remove(obj);
    });
    canvas.discardActiveObject();
    canvas.renderAll();
    onObjectModified?.();
  }, [onObjectModified, getBackgroundImage]);

  /** Set canvas zoom level, keeping content centered */
  const setZoom = useCallback(
    (zoom: number) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas || !wrapper) return;

      const vpW = wrapper.clientWidth;
      const vpH = wrapper.clientHeight;
      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;

      const panX = (vpW - imgW * zoom) / 2;
      const panY = (vpH - imgH * zoom) / 2;
      canvas.setViewportTransform([zoom, 0, 0, zoom, panX, panY]);
      if (marqueeRef.current.obj) {
        applyMarqueeStroke(canvas, marqueeRef.current.obj);
        for (const o of marqueeRef.current.extraObjs) applyMarqueeStroke(canvas, o);
      }
      canvas.renderAll();
      updateViewportInfo();
    },
    [wrapperRef, updateViewportInfo],
  );

  /** Fit canvas to container (re-center and scale to fit) */
  const fitToContainer = useCallback(() => {
    return zoomToFit();
  }, [zoomToFit]);

  /** Rotate the entire canvas by degrees */
  const rotateCanvas = useCallback(
    (degrees: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.getObjects().forEach((obj) => {
        const currentAngle = obj.angle || 0;
        obj.rotate(currentAngle + degrees);
      });
      canvas.renderAll();
      onObjectModified?.();
    },
    [onObjectModified],
  );

  /** Flip canvas objects */
  const flipCanvas = useCallback(
    (direction: 'horizontal' | 'vertical') => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.getObjects().forEach((obj) => {
        if (direction === 'horizontal') {
          obj.set('flipX', !obj.flipX);
        } else {
          obj.set('flipY', !obj.flipY);
        }
      });
      canvas.renderAll();
      onObjectModified?.();
    },
    [onObjectModified],
  );

  /** Export canvas to data URL */
  const exportToDataURL = useCallback((format: 'png' | 'jpeg' | 'webp' = 'png', quality = 1) => {
    const canvas = canvasRef.current;
    if (!canvas) return '';
    return canvas.toDataURL({ format, quality, multiplier: 1 });
  }, []);

  /** Export canvas to blob */
  const exportToBlob = useCallback(
    (format: 'png' | 'jpeg' | 'webp' = 'png', quality = 1): Promise<Blob | null> => {
      const canvas = canvasRef.current;
      if (!canvas) return Promise.resolve(null);
      return new Promise((resolve) => {
        const dataUrl = canvas.toDataURL({ format, quality, multiplier: 1 });
        fetch(dataUrl)
          .then((res) => res.blob())
          .then(resolve)
          .catch(() => resolve(null));
      });
    },
    [],
  );

  /** Serialize canvas to JSON, optionally including custom property names. */
  const toJSON = useCallback((propertiesToInclude?: string[]) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    // Fabric's `toJSON()` is typed without args, but `toObject` accepts the
    // properties array and produces the same JSON-serializable structure.
    return propertiesToInclude && propertiesToInclude.length > 0
      ? canvas.toObject(propertiesToInclude)
      : canvas.toJSON();
  }, []);

  /**
   * Load canvas from JSON. Sets background image refs and dimensions but does
   * NOT modify the viewport transform — the caller decides whether to fit
   * (`fitToContainer`) or restore a previously saved transform
   * (`setViewportTransform`).
   */
  const loadFromJSON = useCallback(
    async (json: object): Promise<void> => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      await canvas.loadFromJSON(json);

      const objects = canvas.getObjects();
      const bgImg = objects.find(
        (o) =>
          o instanceof fabric.FabricImage &&
          (isBackgroundImage(o) || !(o as fabric.FabricImage & { selectable: boolean }).selectable),
      ) as fabric.FabricImage | undefined;

      if (bgImg) {
        // Tag for future lookups, normalize to non-selectable until the user
        // explicitly clicks the Background layer.
        tagAsBackground(bgImg);
        bgImg.set({ selectable: false, evented: false });
        backgroundImageRef.current = bgImg;
        const imgW = bgImg.width || 1;
        const imgH = bgImg.height || 1;
        imageDimsRef.current = { width: imgW, height: imgH };
        canvas.sendObjectToBack(bgImg);
        onCanvasDimensions?.(imgW, imgH);
      }

      // Convert every layer-tagged FabricImage's underlying element to a
      // canvas so it's paintable, and register the surfaces in the map.
      // (Restored sessions have HTMLImageElements after deserialization;
      // we need HTMLCanvasElements to draw onto.) Disable object caching so
      // live writes to the canvas reflect without manual invalidation.
      paintSurfacesRef.current.clear();
      for (const obj of canvas.getObjects()) {
        if (!(obj instanceof fabric.FabricImage)) continue;
        const layerId = getObjectLayerId(obj);
        if (!layerId) continue;
        const w = obj.width ?? 0;
        const h = obj.height ?? 0;
        const surfaceCanvas = ensureCanvasElement(
          obj.getElement() as HTMLImageElement | HTMLCanvasElement,
          w,
          h,
        );
        if (obj.getElement() !== surfaceCanvas) obj.setElement(surfaceCanvas);
        obj.set({ objectCaching: false });
        paintSurfacesRef.current.set(layerId, { canvas: surfaceCanvas, fabricImg: obj });
      }

      canvas.renderAll();
    },
    [onCanvasDimensions],
  );

  /** Apply a saved [a, b, c, d, e, f] viewport transform. Returns the resulting zoom. */
  const setViewportTransform = useCallback(
    (vt: number[]): number => {
      const canvas = canvasRef.current;
      if (!canvas || vt.length !== 6) return 1;
      canvas.setViewportTransform([vt[0], vt[1], vt[2], vt[3], vt[4], vt[5]]);
      if (marqueeRef.current.obj) {
        applyMarqueeStroke(canvas, marqueeRef.current.obj);
        for (const o of marqueeRef.current.extraObjs) applyMarqueeStroke(canvas, o);
      }
      canvas.renderAll();
      updateViewportInfo();
      return vt[0];
    },
    [updateViewportInfo],
  );

  /** Snapshot the current viewport transform for persistence. */
  const getViewportTransform = useCallback((): number[] | null => {
    const canvas = canvasRef.current;
    const vt = canvas?.viewportTransform;
    return vt ? [...vt] : null;
  }, []);

  /** Clear the active marquee selection (any shape) if present */
  const clearMarquee = useCallback(() => {
    const canvas = canvasRef.current;
    const m = marqueeRef.current;
    if (!canvas) return;
    if (m.obj || m.extraObjs.length > 0) {
      if (m.obj) canvas.remove(m.obj);
      for (const o of m.extraObjs) canvas.remove(o);
      m.obj = null;
      m.extraObjs = [];
      m.points = [];
      m.wandMaskCanvas = null;
      m.isDrawing = false;
      canvas.renderAll();
      setHasMarqueeSelection(false);
    }
  }, []);

  /**
   * Render the marqueed region of the active layer's paint surface to a
   * fresh HTMLCanvasElement sized to the marquee's bounding box. Used by
   * copySelection / cutSelection. Returns null if there's no finalized
   * marquee, no active layer, or the active layer doesn't yet have a
   * paint surface.
   */
  const renderMarqueeRegion = useCallback((): {
    canvas: HTMLCanvasElement;
    left: number;
    top: number;
  } | null => {
    const m = marqueeRef.current;
    const layerId = activeLayerIdRef.current;
    if (!m.obj || m.isDrawing || !layerId) return null;
    const surface = paintSurfacesRef.current.get(layerId);
    if (!surface) return null;

    // Compute the bbox via the pure helper (intrinsic properties, not the
    // stale `getBoundingRect()` cache). See the helper's doc comment for
    // why and the unit tests in `lib/marquee-bounds.test.ts`.
    const bounds = (() => {
      if (m.shape === 'rect') {
        const r = m.obj as fabric.Rect;
        return computeMarqueeBounds({
          shape: 'rect',
          left: r.left ?? 0,
          top: r.top ?? 0,
          width: r.width ?? 0,
          height: r.height ?? 0,
        });
      }
      if (m.shape === 'ellipse') {
        const e = m.obj as fabric.Ellipse;
        return computeMarqueeBounds({
          shape: 'ellipse',
          left: e.left ?? 0,
          top: e.top ?? 0,
          rx: e.rx ?? 0,
          ry: e.ry ?? 0,
        });
      }
      if (m.shape === 'lasso' || m.shape === 'polygon') {
        return computeMarqueeBounds({ shape: m.shape, points: m.points });
      }
      if (m.shape === 'wand') {
        const polygons = [m.obj, ...m.extraObjs]
          .filter((p): p is fabric.Polygon => p instanceof fabric.Polygon)
          .map((p) => p.points ?? []);
        return computeMarqueeBounds({ shape: 'wand', polygons });
      }
      return null;
    })();
    if (!bounds) return null;
    const w = Math.round(bounds.width);
    const h = Math.round(bounds.height);
    if (w <= 0 || h <= 0) return null;

    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    const ctx = out.getContext('2d');
    if (!ctx) return null;

    // Step 1: Copy the bbox region from the source surface into `out` using
    // the explicit 9-arg drawImage form. surface.canvas pixel (X,Y) =
    // scene (X + surfaceLeft, Y + surfaceTop), so the source rect in surface
    // pixels is (bounds.left - surfaceLeft, bounds.top - surfaceTop).
    const surfaceLeft = surface.fabricImg.left ?? 0;
    const surfaceTop = surface.fabricImg.top ?? 0;
    const sx = Math.round(bounds.left - surfaceLeft);
    const sy = Math.round(bounds.top - surfaceTop);
    ctx.drawImage(surface.canvas, sx, sy, w, h, 0, 0, w, h);

    // Step 2: For non-rect marquees, mask out pixels outside the selection
    // shape. We render the marquee shape into a separate mask canvas in
    // out-local coords and apply it via destination-in compositing.
    if (m.shape !== 'rect') {
      const mask = document.createElement('canvas');
      mask.width = w;
      mask.height = h;
      const mCtx = mask.getContext('2d');
      if (mCtx) {
        mCtx.fillStyle = '#ffffff';
        if (m.shape === 'ellipse') {
          const e = m.obj as fabric.Ellipse;
          const rx = e.rx ?? 0;
          const ry = e.ry ?? 0;
          const cx = (e.left ?? 0) + rx - bounds.left;
          const cy = (e.top ?? 0) + ry - bounds.top;
          mCtx.beginPath();
          mCtx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
          mCtx.fill();
        } else if ((m.shape === 'lasso' || m.shape === 'polygon') && m.points.length >= 3) {
          mCtx.beginPath();
          mCtx.moveTo(m.points[0].x - bounds.left, m.points[0].y - bounds.top);
          for (let i = 1; i < m.points.length; i++) {
            mCtx.lineTo(m.points[i].x - bounds.left, m.points[i].y - bounds.top);
          }
          mCtx.closePath();
          mCtx.fill();
        } else if (m.shape === 'wand' && m.wandMaskCanvas) {
          // Crop the wand mask (full bg-sized) into the bbox region.
          mCtx.drawImage(
            m.wandMaskCanvas,
            Math.round(bounds.left),
            Math.round(bounds.top),
            w,
            h,
            0,
            0,
            w,
            h,
          );
        }
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(mask, 0, 0);
        ctx.globalCompositeOperation = 'source-over';
      }
    }

    return { canvas: out, left: bounds.left, top: bounds.top };
  }, []);

  /** Copy the marqueed region of the active layer to the clipboard. Returns
   *  true on success. */
  const copySelection = useCallback((): boolean => {
    const result = renderMarqueeRegion();
    if (!result) return false;
    clipboardRef.current = result;
    setHasClipboard(true);
    return true;
  }, [renderMarqueeRegion]);

  /** Copy + clear: same as copySelection, then clip-clear the marqueed
   *  pixels from the active layer's paint surface. Returns true on success. */
  const cutSelection = useCallback((): boolean => {
    const result = renderMarqueeRegion();
    if (!result) return false;
    clipboardRef.current = result;
    setHasClipboard(true);

    const canvas = canvasRef.current;
    const m = marqueeRef.current;
    const layerId = activeLayerIdRef.current;
    if (!canvas || !layerId) return true;
    const surface = paintSurfacesRef.current.get(layerId);
    if (!surface) return true;
    const ctx = surface.canvas.getContext('2d');
    if (!ctx) return true;

    const offsetX = -(surface.fabricImg.left ?? 0);
    const offsetY = -(surface.fabricImg.top ?? 0);
    ctx.save();
    ctx.translate(offsetX, offsetY);
    if (m.shape === 'wand' && m.wandMaskCanvas) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.drawImage(m.wandMaskCanvas, 0, 0);
    } else {
      const clipped = applyMarqueeClipToContext(ctx, m);
      // The clearRect bounds are over-broad on purpose: after the
      // translate, this rect covers a shifted region that's NOT exactly
      // the surface's pixel bounds, but the active clip narrows the
      // actual cleared area to just the marquee shape. We just need any
      // rect that fully encloses the clip path.
      if (clipped) ctx.clearRect(0, 0, surface.canvas.width, surface.canvas.height);
    }
    ctx.restore();

    surface.fabricImg.setElement(surface.canvas);
    surface.fabricImg.dirty = true;
    surface.fabricImg.setCoords();
    canvas.renderAll();
    return true;
  }, [renderMarqueeRegion]);

  /** Read the clipboard payload — caller (EditorPage) is expected to
   *  create a new layer and place a fabric.FabricImage there. Returns
   *  null when the clipboard is empty. */
  const getClipboard = useCallback(() => clipboardRef.current, []);

  /** Switch the marquee shape, clearing any in-progress selection. No-op if unchanged. */
  const setMarqueeShape = useCallback(
    (shape: MarqueeShape) => {
      if (marqueeRef.current.shape === shape) return;
      clearMarquee();
      marqueeRef.current.shape = shape;
    },
    [clearMarquee],
  );

  /** Tag the current background image with a layer id (used to associate the
   *  bg image with the user-facing "Background" layer entry). No-op if there's
   *  no bg yet. */
  const setBackgroundLayerId = useCallback(
    (layerId: string) => {
      const bg = getBackgroundImage();
      if (!bg) return;
      setObjectLayerId(bg, layerId);
      // Register the bg's underlying canvas as the Background layer's paint
      // surface so subsequent strokes route there.
      const w = bg.width ?? 0;
      const h = bg.height ?? 0;
      const surfaceCanvas = ensureCanvasElement(
        bg.getElement() as HTMLImageElement | HTMLCanvasElement,
        w,
        h,
      );
      if (bg.getElement() !== surfaceCanvas) bg.setElement(surfaceCanvas);
      paintSurfacesRef.current.set(layerId, { canvas: surfaceCanvas, fabricImg: bg });
    },
    [getBackgroundImage],
  );

  /** Toggle visibility of every Fabric object tagged with the given layerId. */
  const setLayerVisibility = useCallback((layerId: string, visible: boolean) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let changed = false;
    for (const o of canvas.getObjects()) {
      if (getObjectLayerId(o) === layerId) {
        o.visible = visible;
        changed = true;
      }
    }
    if (changed) canvas.renderAll();
  }, []);

  /**
   * Look up (or lazily create) a paint surface for the given layer. The
   * Background layer's surface is the bg image's underlying canvas; other
   * layers get a transparent canvas the size of the bg image, wrapped in a
   * FabricImage tagged with the layer id.
   *
   * Returns null only if there's no canvas / no bg image / no dimensions.
   */
  const ensurePaintSurface = useCallback(
    (layerId: string): { canvas: HTMLCanvasElement; fabricImg: fabric.FabricImage } | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const cached = paintSurfacesRef.current.get(layerId);
      if (cached && canvas.contains(cached.fabricImg)) return cached;

      // Bg-layer fast path: locate the bg image, ensure its element is a
      // canvas, and register.
      const bg = getBackgroundImage();
      if (bg && getObjectLayerId(bg) === layerId) {
        const w = bg.width ?? 0;
        const h = bg.height ?? 0;
        const surfaceCanvas = ensureCanvasElement(
          bg.getElement() as HTMLImageElement | HTMLCanvasElement,
          w,
          h,
        );
        if (bg.getElement() !== surfaceCanvas) bg.setElement(surfaceCanvas);
        const entry = { canvas: surfaceCanvas, fabricImg: bg };
        paintSurfacesRef.current.set(layerId, entry);
        return entry;
      }

      // Otherwise create a fresh transparent canvas + FabricImage.
      const dims = imageDimsRef.current;
      if (dims.width <= 0 || dims.height <= 0) return null;
      const surfaceCanvas = document.createElement('canvas');
      surfaceCanvas.width = dims.width;
      surfaceCanvas.height = dims.height;
      const fabricImg = new fabric.FabricImage(surfaceCanvas, {
        left: 0,
        top: 0,
        scaleX: 1,
        scaleY: 1,
        selectable: false,
        evented: false,
        objectCaching: false,
      });
      (fabricImg as fabric.FabricImage & { erasable: boolean }).erasable = false;
      (fabricImg as fabric.FabricImage & { id: string }).id = `paint_${layerId}`;
      setObjectLayerId(fabricImg, layerId);
      canvas.add(fabricImg);

      const entry = { canvas: surfaceCanvas, fabricImg };
      paintSurfacesRef.current.set(layerId, entry);
      return entry;
    },
    [getBackgroundImage],
  );
  ensurePaintSurfaceRef.current = ensurePaintSurface;

  /**
   * Render a Fabric Path onto a paint surface's canvas, applying the active
   * marquee as a clip and the eraser's destination-out compositing if the
   * eraser tool was used.
   *
   * Implementation: render the path onto a *temporary* StaticCanvas to get a
   * clean Fabric rendering pipeline (transforms, brush settings, anti-
   * aliasing) with no shared state with the main canvas. Then `drawImage` the
   * resulting bitmap onto our surface with the appropriate compositing/clip.
   * This avoids cache contamination from rendering Path objects directly onto
   * the surface's 2D context.
   *
   * The path is removed from the main canvas before being added to the temp
   * canvas (Fabric objects belong to one canvas at a time).
   */
  const bakeStrokeOntoSurface = useCallback(
    (path: fabric.Object, layerId: string, isEraser: boolean) => {
      const surface = ensurePaintSurface(layerId);
      if (!surface) return false;
      const ctx = surface.canvas.getContext('2d');
      if (!ctx) return false;

      const w = surface.canvas.width;
      const h = surface.canvas.height;

      // Render the path onto a fresh temp canvas via Fabric's pipeline. The
      // path is expected to already be removed from the main canvas by the
      // caller.
      const tempEl = document.createElement('canvas');
      tempEl.width = w;
      tempEl.height = h;
      const tempCanvas = new fabric.StaticCanvas(tempEl, {
        width: w,
        height: h,
        enableRetinaScaling: false,
        renderOnAddRemove: false,
        backgroundColor: 'transparent',
      });
      tempCanvas.add(path);
      tempCanvas.renderAll();

      const m = marqueeRef.current;
      const hasMarquee = !!m.obj && !m.isDrawing;

      // Strokes are authored in scene coordinates, but the surface canvas's
      // (0,0) corresponds to the paint surface's current world position. If
      // the layer has been moved (left/top != 0), shift the context so scene
      // coords map to the right surface pixels.
      const offsetX = -(surface.fabricImg.left ?? 0);
      const offsetY = -(surface.fabricImg.top ?? 0);

      // For eraser strokes, decouple the bake's alpha from the live preview
      // color (a fixed semi-transparent gray). Build a fresh mask where any
      // stroke pixel becomes opaque at the user's eraser-opacity setting,
      // so 1.0 fully erases and 0.5 reduces alpha by 50%, regardless of
      // the preview color's own alpha. For draw strokes we use tempEl
      // unchanged so opacity-aware blending keeps working.
      const stamp = isEraser ? buildEraserMask(tempEl, eraserOpacityRef.current) : tempEl;

      ctx.save();
      ctx.translate(offsetX, offsetY);
      if (hasMarquee && m.shape === 'wand' && m.wandMaskCanvas) {
        const masked = document.createElement('canvas');
        masked.width = w;
        masked.height = h;
        const mCtx = masked.getContext('2d');
        if (mCtx) {
          mCtx.drawImage(stamp, 0, 0);
          mCtx.globalCompositeOperation = 'destination-in';
          mCtx.drawImage(m.wandMaskCanvas, 0, 0);
          if (isEraser) ctx.globalCompositeOperation = 'destination-out';
          ctx.drawImage(masked, 0, 0);
        }
      } else {
        if (hasMarquee) applyMarqueeClipToContext(ctx, m);
        if (isEraser) ctx.globalCompositeOperation = 'destination-out';
        ctx.drawImage(stamp, 0, 0);
      }
      ctx.restore();

      tempCanvas.dispose();

      // Force Fabric to re-bind the FabricImage to its (now-modified) source
      // canvas. setElement clears internal texture handles and runs
      // `_initConfig`, which is what makes new pixels show up reliably across
      // multiple consecutive bakes — `dirty = true` alone isn't sufficient.
      surface.fabricImg.setElement(surface.canvas);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();
      return true;
    },
    [ensurePaintSurface],
  );

  /**
   * Reorder canvas objects to match the layer list. `orderedLayerIds[0]` is the
   * top-of-panel / front-of-canvas layer. Objects without a layerId keep their
   * relative positions; marquee and crop overlays are always brought to front.
   */
  const applyLayerOrder = useCallback((orderedLayerIds: readonly string[]) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Iterate from back-most to front-most. bringObjectToFront leaves the most
    // recently brought item on top, so the last id processed ends up at the top.
    for (let i = orderedLayerIds.length - 1; i >= 0; i--) {
      const layerId = orderedLayerIds[i];
      for (const obj of canvas.getObjects()) {
        if (getObjectLayerId(obj) === layerId) {
          canvas.bringObjectToFront(obj);
        }
      }
    }
    // Keep UI overlays above all layer content.
    for (const obj of canvas.getObjects()) {
      if (
        (obj as unknown as { _isMarquee?: boolean })._isMarquee ||
        (obj as unknown as { _isCropRect?: boolean })._isCropRect
      ) {
        canvas.bringObjectToFront(obj);
      }
    }
    canvas.renderAll();
  }, []);

  /**
   * Select the canvas objects tagged with the given layerId. Single object →
   * setActiveObject; multiple → ActiveSelection. Forces target objects to be
   * selectable + evented so the transform handles render even if a previous
   * tool (marquee, crop) had locked them. The bg image is left non-selectable
   * to match Photoshop's locked-Background behavior.
   */
  const selectLayerObjects = useCallback(
    (layerId: string) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const bg = getBackgroundImage();
      // Strokes are part of the layer but not individually selectable; skip them
      // so the active selection wraps only the user-movable objects.
      const objs = canvas.getObjects().filter((o) => getObjectLayerId(o) === layerId);
      canvas.discardActiveObject();

      // Toggle the bg image's interactivity based on whether the target layer
      // *is* the Background. Selecting Background unlocks it; selecting any
      // other layer re-locks it so it's not accidentally clicked on the canvas.
      if (bg) {
        const targetingBg = objs.includes(bg);
        bg.set({ selectable: targetingBg, evented: targetingBg });
      }

      for (const o of objs) {
        o.set({ selectable: true, evented: true });
      }

      if (objs.length === 1) {
        canvas.setActiveObject(objs[0]);
        // For text layers: drop straight into edit mode with the existing
        // text selected so typing replaces it.
        if (objs[0] instanceof fabric.IText) {
          objs[0].enterEditing();
          objs[0].selectAll();
        }
      } else if (objs.length > 1) {
        const sel = new fabric.ActiveSelection(objs, { canvas });
        canvas.setActiveObject(sel);
      }
      canvas.requestRenderAll();
    },
    [getBackgroundImage],
  );

  /** Remove every Fabric object tagged with the given layerId, including its
   *  paint surface entry. */
  const removeLayerObjects = useCallback(
    (layerId: string) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const toRemove = canvas.getObjects().filter((o) => getObjectLayerId(o) === layerId);
      paintSurfacesRef.current.delete(layerId);
      if (toRemove.length === 0) return;
      for (const o of toRemove) canvas.remove(o);
      canvas.discardActiveObject();
      canvas.renderAll();
      onObjectModified?.();
    },
    [onObjectModified],
  );

  /** Add a FabricImage from a Blob (e.g. clipboard paste) tagged with the
   *  given layerId. Returns the created object or null. */
  const addImageLayer = useCallback(
    async (blob: Blob, layerId: string): Promise<fabric.FabricImage | null> => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
      const img = await fabric.FabricImage.fromURL(dataUrl, { crossOrigin: 'anonymous' });
      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;
      img.set({
        left: imgW / 2,
        top: imgH / 2,
        originX: 'center',
        originY: 'center',
      });
      (img as fabric.FabricImage & { id: string }).id = `pasted_${Date.now()}`;
      setObjectLayerId(img, layerId);
      canvas.add(img);
      canvas.setActiveObject(img);
      canvas.renderAll();
      // Snapshot is the caller's responsibility (see addText comment).
      return img;
    },
    [],
  );

  /** Whether to force rect/ellipse marquee drawing to a square aspect ratio. */
  const setForceSquare = useCallback((force: boolean) => {
    forceSquareRef.current = force;
  }, []);

  /** Update the marquee outline color. Re-tints any active marquee object live. */
  const setMarqueeColor = useCallback((color: string) => {
    marqueeColorRef.current = color;
    const canvas = canvasRef.current;
    const m = marqueeRef.current;
    if (canvas && (m.obj || m.extraObjs.length)) {
      if (m.obj) applyMarqueeColor(m.obj, color);
      for (const o of m.extraObjs) applyMarqueeColor(o, color);
      canvas.renderAll();
    }
  }, []);

  /** Update magic-wand tolerance and contiguous mode. */
  const setWandSettings = useCallback((tolerance: number, contiguous: boolean) => {
    wandToleranceRef.current = tolerance;
    wandContiguousRef.current = contiguous;
  }, []);

  /** Tell the canvas which layer is currently active. New paint strokes get
   *  tagged with this layerId so they're associated with that layer in the
   *  layers panel and follow its visibility / order / deletion. */
  const setActiveLayerId = useCallback((id: string | null) => {
    activeLayerIdRef.current = id;
  }, []);

  /** Push the latest brush/fill color so the fill tool can pick it up. */
  const setBrushColor = useCallback((color: string) => {
    brushColorRef.current = color;
  }, []);

  /** Set the active tool on the canvas */
  const setTool = useCallback(
    (tool: EditorTool, brushSettings?: BrushSettings) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      activeToolRef.current = tool;

      // Preserve the active marquee for tools that operate on the selection
      // (Move, Draw, Eraser, Fill, Eyedropper) plus the marquee tool itself.
      if (!MARQUEE_PRESERVING_TOOLS.has(tool)) {
        clearMarquee();
      }

      // Tear down the crop rect if leaving crop mode without applying.
      if (tool !== 'crop' && cropRectRef.current) {
        canvas.discardActiveObject();
        canvas.remove(cropRectRef.current);
        cropRectRef.current = null;
      }

      canvas.isDrawingMode = false;
      canvas.selection = false;
      canvas.defaultCursor = 'default';

      switch (tool) {
        case 'move': {
          canvas.defaultCursor = 'grab';
          const bg = getBackgroundImage();
          canvas.getObjects().forEach((o) => {
            if (o !== bg && !isMarqueeObject(o)) {
              o.set({ selectable: true, evented: true });
            }
          });
          break;
        }
        case 'marquee': {
          canvas.defaultCursor = 'crosshair';
          const bg = getBackgroundImage();
          // Force the bg non-evented so its hit-test doesn't intercept
          // marquee mouse:down (otherwise selecting the Background layer
          // earlier would block subsequent marquee drawing).
          if (bg) bg.set({ selectable: false, evented: false });
          canvas.getObjects().forEach((o) => {
            if (o !== bg && !isMarqueeObject(o)) {
              o.set({ selectable: false, evented: false });
            }
          });
          break;
        }
        case 'draw':
          if (brushSettings) enableDrawMode(brushSettings);
          break;
        case 'eraser':
          canvas.isDrawingMode = true;
          canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
          // Semi-transparent neutral gray for the live preview — visible on
          // both light and dark backgrounds. The actual erase happens in
          // bakeStrokeOntoSurface via `globalCompositeOperation:
          // 'destination-out'`, so the preview color doesn't affect output.
          canvas.freeDrawingBrush.color = 'rgba(128, 128, 128, 0.5)';
          canvas.freeDrawingBrush.width = brushSettings?.width ?? 20;
          break;
        case 'crop': {
          canvas.defaultCursor = 'default';
          canvas.selection = true;
          const bg = getBackgroundImage();
          // Lock bg so its hit-test doesn't compete with the crop rect.
          if (bg) bg.set({ selectable: false, evented: false });
          canvas.getObjects().forEach((o) => {
            if (o !== bg && !isMarqueeObject(o)) {
              o.set({ selectable: false, evented: false });
            }
          });
          const imgW = imageDimsRef.current.width;
          const imgH = imageDimsRef.current.height;
          if (bg && imgW > 0 && imgH > 0) {
            const rect = buildCropRect(canvas, imgW, imgH);
            canvas.add(rect);
            canvas.bringObjectToFront(rect);
            canvas.setActiveObject(rect);
            cropRectRef.current = rect;
            canvas.requestRenderAll();
          }
          break;
        }
        case 'text':
          canvas.defaultCursor = 'text';
          break;
        case 'shape':
          canvas.defaultCursor = 'crosshair';
          break;
        case 'eyedropper':
          // Standard system arrow — its hotspot is at the visible tip, so
          // the pixel sample point is precisely where the arrow points.
          canvas.defaultCursor = 'default';
          break;
        case 'fill':
          canvas.defaultCursor = FILL_CURSOR;
          break;
      }

      canvas.renderAll();
    },
    [enableDrawMode, getBackgroundImage],
  );

  /** Apply crop to the canvas */
  const applyCrop = useCallback(
    (rect: { left: number; top: number; width: number; height: number }) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const clipRect = new fabric.Rect({
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        absolutePositioned: true,
      });

      canvas.getObjects().forEach((obj) => {
        obj.clipPath = clipRect;
      });

      canvas.renderAll();
      onObjectModified?.();
    },
    [onObjectModified],
  );

  /**
   * Apply the active crop rect: extract the chosen region into a new background
   * image, translate overlay objects to the new origin, update canvas dimensions,
   * and reset the crop rect to the new full-image bounds so the user can iterate.
   */
  const applyActiveCrop = useCallback((): boolean => {
    const canvas = canvasRef.current;
    const bg = getBackgroundImage();
    const cropRect = cropRectRef.current;
    if (!canvas || !bg || !cropRect) return false;

    const left = cropRect.left ?? 0;
    const top = cropRect.top ?? 0;
    const width = (cropRect.width ?? 0) * (cropRect.scaleX ?? 1);
    const height = (cropRect.height ?? 0) * (cropRect.scaleY ?? 1);

    const imgW = bg.width ?? 0;
    const imgH = bg.height ?? 0;
    const cropX = Math.max(0, Math.round(left));
    const cropY = Math.max(0, Math.round(top));
    const cropW = Math.min(imgW - cropX, Math.round(width));
    const cropH = Math.min(imgH - cropY, Math.round(height));
    if (cropW <= 0 || cropH <= 0) return false;

    const imgEl = bg.getElement() as HTMLImageElement | HTMLCanvasElement;
    const offscreen = document.createElement('canvas');
    offscreen.width = cropW;
    offscreen.height = cropH;
    const ctx = offscreen.getContext('2d');
    if (!ctx) return false;
    ctx.drawImage(imgEl, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

    canvas.discardActiveObject();
    canvas.remove(cropRect);
    cropRectRef.current = null;

    canvas.getObjects().forEach((obj) => {
      if (obj === bg) return;
      obj.set({ left: (obj.left ?? 0) - cropX, top: (obj.top ?? 0) - cropY });
      obj.setCoords();
    });

    const newImg = new fabric.FabricImage(offscreen, {
      left: 0,
      top: 0,
      scaleX: 1,
      scaleY: 1,
      selectable: false,
      evented: false,
    });
    (newImg as fabric.FabricImage & { erasable: boolean }).erasable = false;
    tagAsBackground(newImg);
    newImg.set({ objectCaching: false } as Partial<fabric.FabricImage>);
    newImg.filters = bg.filters ? [...bg.filters] : [];
    newImg.applyFilters();

    // Carry the bg's layer id forward to the new image and re-register
    // its underlying canvas as the Background layer's paint surface.
    // Without this, draw / eraser / fill strokes would silently bake into
    // the orphaned old surface canvas and never reach the visible bg.
    const bgLayerId = getObjectLayerId(bg);
    if (bgLayerId) {
      setObjectLayerId(newImg, bgLayerId);
      paintSurfacesRef.current.set(bgLayerId, { canvas: offscreen, fabricImg: newImg });
    }

    canvas.remove(bg);
    canvas.add(newImg);
    canvas.sendObjectToBack(newImg);
    backgroundImageRef.current = newImg;
    imageDimsRef.current = { width: cropW, height: cropH };
    onCanvasDimensions?.(cropW, cropH);

    zoomToFit();

    // Re-create crop rect at the new full bounds so the user can crop again.
    const fresh = buildCropRect(canvas, cropW, cropH);
    canvas.add(fresh);
    canvas.setActiveObject(fresh);
    cropRectRef.current = fresh;

    canvas.renderAll();
    onObjectModified?.();
    return true;
  }, [onCanvasDimensions, onObjectModified, zoomToFit, getBackgroundImage]);

  /**
   * Remove the background of the current image using @imgly/background-removal,
   * which runs ONNX inference fully in the browser. Replaces the existing
   * background image with a transparent-PNG result. The library is loaded
   * dynamically so it doesn't bloat the editor's initial bundle.
   */
  const removeBackground = useCallback(async () => {
    const canvas = canvasRef.current;
    const img = getBackgroundImage();
    if (!canvas || !img) return;

    const imgEl = img.getElement() as HTMLImageElement | HTMLCanvasElement;
    const w = img.width || 0;
    const h = img.height || 0;
    if (!w || !h) return;

    const offscreen = document.createElement('canvas');
    offscreen.width = w;
    offscreen.height = h;
    const ctx = offscreen.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(imgEl, 0, 0);

    const sourceBlob: Blob | null = await new Promise((resolve) =>
      offscreen.toBlob(resolve, 'image/png'),
    );
    if (!sourceBlob) return;

    const { removeBackground: imglyRemoveBackground } = await import('@imgly/background-removal');
    const resultBlob = await imglyRemoveBackground(sourceBlob);
    const resultUrl = URL.createObjectURL(resultBlob);

    try {
      const newImg = await fabric.FabricImage.fromURL(resultUrl, { crossOrigin: 'anonymous' });
      newImg.set({
        left: 0,
        top: 0,
        scaleX: 1,
        scaleY: 1,
        selectable: false,
        evented: false,
        objectCaching: false,
      } as Partial<fabric.FabricImage>);
      (newImg as fabric.FabricImage & { erasable: boolean }).erasable = false;
      tagAsBackground(newImg);

      // Convert the loaded <img> element to an HTMLCanvasElement so the
      // new bg is paintable. Subsequent draw / eraser / fill strokes need
      // a canvas surface to bake into; without this swap they'd silently
      // no-op (the rendering flips to a non-canvas element after the
      // background-removal step).
      const newW = newImg.width || 1;
      const newH = newImg.height || 1;
      const surfaceCanvas = ensureCanvasElement(
        newImg.getElement() as HTMLImageElement | HTMLCanvasElement,
        newW,
        newH,
      );
      if (newImg.getElement() !== surfaceCanvas) newImg.setElement(surfaceCanvas);

      newImg.filters = img.filters ? [...img.filters] : [];
      newImg.applyFilters();

      // Carry the bg's layer id forward to the replacement and update the
      // paint-surface registry to point at the new canvas. The old entry
      // (pointing to img's now-orphaned element) would otherwise eat all
      // future strokes silently.
      const bgLayerId = getObjectLayerId(img);
      if (bgLayerId) {
        setObjectLayerId(newImg, bgLayerId);
        paintSurfacesRef.current.set(bgLayerId, { canvas: surfaceCanvas, fabricImg: newImg });
      }

      canvas.remove(img);
      canvas.add(newImg);
      canvas.sendObjectToBack(newImg);
      backgroundImageRef.current = newImg;
      canvas.renderAll();
      onObjectModified?.();
    } finally {
      URL.revokeObjectURL(resultUrl);
    }
  }, [onObjectModified, getBackgroundImage]);

  /**
   * Clear every object on the canvas and reset our internal references
   * (background image, paint surfaces, image dimensions, marquee). Used by
   * the multi-tab flow when switching to a freshly-created tab that hasn't
   * had a file loaded into it yet.
   */
  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.clear();
    backgroundImageRef.current = null;
    paintSurfacesRef.current.clear();
    imageDimsRef.current = { width: 0, height: 0 };
    marqueeRef.current.obj = null;
    marqueeRef.current.extraObjs = [];
    marqueeRef.current.points = [];
    marqueeRef.current.wandMaskCanvas = null;
    marqueeRef.current.isDrawing = false;
    // Mirror into React state so the toolbar's Cut / Copy buttons disable
    // when switching to a freshly-emptied tab. Without this, they'd stay
    // enabled (matching the previous tab's marquee) and silently no-op.
    setHasMarqueeSelection(false);
    canvas.renderAll();
  }, []);

  /** Select all objects (excluding background) */
  const selectAll = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const bg = getBackgroundImage();
    const objects = canvas.getObjects().filter((o) => o !== bg);
    if (objects.length > 0) {
      const selection = new fabric.ActiveSelection(objects, { canvas });
      canvas.setActiveObject(selection);
      canvas.renderAll();
    }
  }, [getBackgroundImage]);

  return {
    canvasRef,
    viewport,
    loadImage,
    enableDrawMode,
    disableDrawMode,
    addText,
    addShape,
    updateActiveShape,
    getActiveShapeSettings,
    updateActiveText,
    getActiveTextSettings,
    applyFilters,
    convertToGrayscale,
    deleteSelected,
    setZoom,
    fitToContainer,
    rotateCanvas,
    flipCanvas,
    exportToDataURL,
    exportToBlob,
    toJSON,
    loadFromJSON,
    setTool,
    applyCrop,
    applyActiveCrop,
    selectAll,
    clearMarquee,
    setMarqueeShape,
    setForceSquare,
    setMarqueeColor,
    setWandSettings,
    setActiveLayerId,
    setBrushColor,
    updateBrush,
    setViewportTransform,
    setBackgroundLayerId,
    setLayerVisibility,
    applyLayerOrder,
    selectLayerObjects,
    removeLayerObjects,
    addImageLayer,
    getViewportTransform,
    removeBackground,
    clearCanvas,
    copySelection,
    cutSelection,
    getClipboard,
    hasMarqueeSelection,
    hasClipboard,
  };
}
